import * as React from 'react';
import { createContext, forwardRef, useState, useRef, useCallback, useEffect, useContext, useMemo, Children, isValidElement, cloneElement, useLayoutEffect, Component } from 'react';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { cn } from '@miror/utils';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import * as LabelPrimitive from '@radix-ui/react-label';
import { Loader2, Check, Search, Info, AlertCircle, AlertTriangle, CheckCircle2, X, Star, ChevronDown, PanelLeft, PanelLeftClose, ChevronRight, Sun, Moon, LogOut, AppWindow, ExternalLink, Building2, ChevronsUpDown, CornerDownLeft, ArrowRight, HelpCircle, Settings, SearchX, Lock, Compass, AlertOctagon, RotateCw, Sparkles, RotateCcw, Settings2, TriangleAlert, MessageCircle, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { formatShortcut } from '@miror/utils/platform';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import * as DialogPrimitive from '@radix-ui/react-dialog';

// src/context/miror-ui-context.tsx
var DefaultLink = ({ to, children, className, target, rel }) => /* @__PURE__ */ jsx("a", { href: to, className, target, rel, children });
var DEFAULT_BRAND = {
  appName: "Glivo App"
};
var MirorUIContext = createContext({
  LinkComponent: DefaultLink,
  brand: DEFAULT_BRAND
});
function MirorUIProvider({ linkComponent, brand, children }) {
  return /* @__PURE__ */ jsx(
    MirorUIContext.Provider,
    {
      value: {
        LinkComponent: linkComponent ?? DefaultLink,
        brand: brand ?? DEFAULT_BRAND
      },
      children
    }
  );
}
function useMirorUI() {
  return useContext(MirorUIContext);
}
function useMirorBrand() {
  return useContext(MirorUIContext).brand;
}
var EMPTY_META = {
  title: null,
  breadcrumb: [],
  description: null
};
var PageMetaContext = createContext(null);
function MirorPageMetaProvider({ children, initial }) {
  const [meta, setMetaState] = useState({
    ...EMPTY_META,
    ...initial
  });
  const setMeta = useCallback((next) => {
    setMetaState((prev) => ({ ...prev, ...next }));
  }, []);
  const value = useMemo(() => ({ meta, setMeta }), [meta, setMeta]);
  return /* @__PURE__ */ jsx(PageMetaContext.Provider, { value, children });
}
function useMirorPageMeta() {
  const ctx = useContext(PageMetaContext);
  return ctx?.meta ?? EMPTY_META;
}
function useHasPageMetaProvider() {
  return useContext(PageMetaContext) !== null;
}
function useSetPageMeta(input) {
  const ctx = useContext(PageMetaContext);
  const setMeta = ctx?.setMeta;
  const inputRef = useRef(input);
  inputRef.current = input;
  const breadcrumbKey = JSON.stringify(input.breadcrumb ?? []);
  useEffect(() => {
    if (!setMeta) return;
    const current = inputRef.current;
    setMeta({
      ...current.title !== void 0 && { title: current.title },
      ...current.breadcrumb !== void 0 && { breadcrumb: current.breadcrumb },
      ...current.description !== void 0 && { description: current.description }
    });
    return () => {
      setMeta({
        ...current.title !== void 0 && { title: null },
        ...current.breadcrumb !== void 0 && { breadcrumb: [] },
        ...current.description !== void 0 && { description: null }
      });
    };
  }, [setMeta, input.title, breadcrumbKey, input.description]);
}
function AppLogoFallback({ className, alt = "Logo" }) {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      role: "img",
      "aria-label": alt,
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "1.5",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      className: cn("inline-block", className),
      children: [
        /* @__PURE__ */ jsx("circle", { cx: "12", cy: "12", r: "10" }),
        /* @__PURE__ */ jsx("line", { x1: "4.93", y1: "19.07", x2: "19.07", y2: "4.93" })
      ]
    }
  );
}
var buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:scale-[0.97]",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive: "bg-destructive-solid text-destructive-solid-foreground hover:bg-destructive-solid/90",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline"
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-6",
        icon: "h-9 w-9"
      }
    },
    defaultVariants: { variant: "default", size: "default" }
  }
);
var Button = forwardRef(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return /* @__PURE__ */ jsx(Comp, { ref, className: cn(buttonVariants({ variant, size }), className), ...props });
  }
);
Button.displayName = "Button";
var badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default: "border-transparent bg-primary text-primary-foreground",
        secondary: "border-transparent bg-secondary text-secondary-foreground",
        outline: "text-foreground",
        destructive: "border-transparent bg-destructive-solid text-destructive-solid-foreground"
      }
    },
    defaultVariants: { variant: "default" }
  }
);
function Badge({ className, variant, ...props }) {
  return /* @__PURE__ */ jsx("div", { className: cn(badgeVariants({ variant }), className), ...props });
}
var STYLE_ID = "miror-pulse-styles";
var STYLES = `
.miror-pulse {
  position: relative;
  display: inline-block;
  aspect-ratio: 418 / 451;
  width: 48px;
  line-height: 0;
  color: #2e6cf6;
}
.miror-pulse svg {
  width: 100%;
  height: 100%;
  display: block;
  overflow: visible;
  transform-origin: center;
  animation: mp-pulse 2s cubic-bezier(.4, 0, .2, 1) infinite;
}
.miror-pulse .mp-arc,
.miror-pulse .mp-bar {
  fill: none;
  stroke-linecap: round;
  stroke-dasharray: 500;
  stroke-dashoffset: 500;
}
.miror-pulse .mp-arc {
  stroke: #ECECE9;
  stroke-width: 22;
  animation: mp-arc 2s cubic-bezier(.45, .05, .2, 1) infinite;
}
.miror-pulse .mp-arc-2 { animation-delay: 80ms; }
.miror-pulse .mp-bar {
  stroke: #2e6cf6;
  stroke-width: 24;
  animation: mp-bar 2s cubic-bezier(.45, .05, .2, 1) infinite;
}
:root:not(.dark) .miror-pulse .mp-arc { stroke: #0a0d18; }
:root:not(.dark) .miror-pulse .mp-bar { stroke: #003B95; }
.miror-pulse--invert .mp-arc { stroke: currentColor; }
.miror-pulse--invert .mp-bar { stroke: #cfdcff; }
@keyframes mp-arc {
  0% { stroke-dashoffset: 500; opacity: 0; }
  6% { opacity: 1; }
  38%, 78% { stroke-dashoffset: 0; opacity: 1; }
  92% { stroke-dashoffset: 0; opacity: 0; }
  100% { stroke-dashoffset: 500; opacity: 0; }
}
@keyframes mp-bar {
  0%, 30% { stroke-dashoffset: 500; opacity: 0; }
  34% { opacity: 1; }
  60%, 78% { stroke-dashoffset: 0; opacity: 1; }
  92% { stroke-dashoffset: 0; opacity: 0; }
  100% { stroke-dashoffset: 500; opacity: 0; }
}
@keyframes mp-pulse {
  0%, 58%, 100% { transform: scale(1); }
  66% { transform: scale(1.035); }
  74% { transform: scale(1); }
}
.miror-pulse--xs { width: 22px; }
.miror-pulse--sm { width: 36px; }
.miror-pulse--md { width: 48px; }
.miror-pulse--lg { width: 96px; }
.miror-pulse--xl { width: 160px; }
.miror-pulse--beam .mp-bar {
  animation:
    mp-bar 2s cubic-bezier(.45, .05, .2, 1) infinite,
    mp-glow 2s ease-in-out infinite;
}
@keyframes mp-glow {
  0%, 60%, 100% { filter: drop-shadow(0 0 0 rgba(46, 108, 246, 0)); }
  68% { filter: drop-shadow(0 0 14px rgba(79, 135, 255, .55)); }
}
.miror-pulse-sr {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
.miror-pulse-overlay {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(ellipse at center, rgba(248, 249, 252, .85), rgba(248, 249, 252, .97) 80%);
  backdrop-filter: blur(6px);
  animation: mp-overlay-in .25s ease-out;
}
:root.dark .miror-pulse-overlay {
  background: radial-gradient(ellipse at center, rgba(5, 8, 16, .85), rgba(5, 8, 16, .97) 80%);
}
@keyframes mp-overlay-in {
  from { opacity: 0; }
  to { opacity: 1; }
}
@media (prefers-reduced-motion: reduce) {
  .miror-pulse svg,
  .miror-pulse .mp-arc,
  .miror-pulse .mp-bar { animation: none; }
  .miror-pulse .mp-arc,
  .miror-pulse .mp-bar { stroke-dashoffset: 0; opacity: 1; }
  .miror-pulse svg { animation: mp-fade 1.6s ease-in-out infinite; }
  @keyframes mp-fade {
    0%, 100% { opacity: 1; }
    50% { opacity: .4; }
  }
}
`;
function injectStyles() {
  if (typeof document === "undefined") return;
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = STYLES;
  document.head.appendChild(el);
}
function cn4(...args) {
  return args.filter(Boolean).join(" ");
}
var MirorPulse = React.forwardRef(
  ({
    size = "md",
    variant = "mono",
    label = "Carregando",
    fullscreen,
    invert,
    className,
    ...rest
  }, ref) => {
    React.useEffect(() => {
      injectStyles();
    }, []);
    const el = /* @__PURE__ */ jsxs(
      "div",
      {
        ref,
        role: "status",
        "aria-label": label,
        className: cn4(
          "miror-pulse",
          `miror-pulse--${size}`,
          variant === "beam" && "miror-pulse--beam",
          invert && "miror-pulse--invert",
          className
        ),
        ...rest,
        children: [
          /* @__PURE__ */ jsxs("svg", { viewBox: "0 0 418 451", "aria-hidden": "true", children: [
            /* @__PURE__ */ jsx(
              "path",
              {
                className: "mp-arc mp-arc-1",
                d: "M 62.33 316.23 A 170 170 0 0 1 305.8 91.56"
              }
            ),
            /* @__PURE__ */ jsx(
              "path",
              {
                className: "mp-arc mp-arc-2",
                d: "M 357.67 147.77 A 170 170 0 0 1 114.2 372.44"
              }
            ),
            /* @__PURE__ */ jsx("line", { className: "mp-bar", x1: "39", y1: "389", x2: "380", y2: "74" })
          ] }),
          /* @__PURE__ */ jsx("span", { className: "miror-pulse-sr", children: label })
        ]
      }
    );
    if (!fullscreen) return el;
    return /* @__PURE__ */ jsx("div", { className: "miror-pulse-overlay", children: el });
  }
);
MirorPulse.displayName = "MirorPulse";
function MirorPulseWithLabel({
  label,
  size = "lg",
  className
}) {
  return /* @__PURE__ */ jsxs("div", { className: cn4("flex flex-col items-center gap-3", className), children: [
    /* @__PURE__ */ jsx(MirorPulse, { size, label }),
    /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: label })
  ] });
}
var Card = forwardRef(
  ({ className, interactive, ...props }, ref) => /* @__PURE__ */ jsx(
    "div",
    {
      ref,
      className: cn(
        "rounded-lg border bg-card text-card-foreground shadow-sm",
        interactive && "cursor-pointer transition-shadow hover:shadow-md hover:-translate-y-[1px]",
        className
      ),
      ...props
    }
  )
);
Card.displayName = "Card";
var CardHeader = forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx("div", { ref, className: cn("flex flex-col space-y-1.5 p-6", className), ...props })
);
CardHeader.displayName = "CardHeader";
var CardTitle = forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx(
    "h3",
    {
      ref,
      className: cn("text-base font-semibold leading-none tracking-tight", className),
      ...props
    }
  )
);
CardTitle.displayName = "CardTitle";
var CardDescription = forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx("p", { ref, className: cn("text-sm text-muted-foreground", className), ...props })
);
CardDescription.displayName = "CardDescription";
var CardContent = forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx("div", { ref, className: cn("p-6 pt-0", className), ...props })
);
CardContent.displayName = "CardContent";
var CardFooter = forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx("div", { ref, className: cn("flex items-center p-6 pt-0", className), ...props })
);
CardFooter.displayName = "CardFooter";
var statusBadgeVariants = cva(
  "inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium",
  {
    variants: {
      tone: {
        success: "bg-success-soft text-success-soft-foreground",
        warning: "bg-warning-soft text-warning-soft-foreground",
        danger: "bg-destructive-soft text-destructive-soft-foreground",
        info: "bg-info-soft text-info-soft-foreground",
        neutral: "bg-muted text-muted-foreground"
      },
      solid: {
        true: "",
        false: ""
      }
    },
    compoundVariants: [
      { tone: "success", solid: true, class: "bg-success-solid text-success-solid-foreground" },
      { tone: "warning", solid: true, class: "bg-warning-solid text-warning-solid-foreground" },
      { tone: "danger", solid: true, class: "bg-destructive-solid text-destructive-solid-foreground" },
      { tone: "info", solid: true, class: "bg-info-solid text-info-solid-foreground" }
    ],
    defaultVariants: { tone: "neutral", solid: false }
  }
);
function StatusBadge({ className, tone, solid, ...props }) {
  return /* @__PURE__ */ jsx("div", { className: cn(statusBadgeVariants({ tone, solid }), className), ...props });
}
var Input = forwardRef(
  ({ className, type, ...props }, ref) => /* @__PURE__ */ jsx(
    "input",
    {
      type,
      ref,
      className: cn(
        "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors",
        "file:border-0 file:bg-transparent file:text-sm file:font-medium",
        "placeholder:text-muted-foreground",
        "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
        "disabled:cursor-not-allowed disabled:opacity-50",
        className
      ),
      ...props
    }
  )
);
Input.displayName = "Input";
var Label = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  LabelPrimitive.Root,
  {
    ref,
    className: cn(
      "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
      className
    ),
    ...props
  }
));
Label.displayName = LabelPrimitive.Root.displayName;
var Textarea = forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx(
    "textarea",
    {
      ref,
      className: cn(
        "flex min-h-[80px] w-full rounded-md border bg-background px-3 py-2 text-sm shadow-sm",
        "placeholder:text-muted-foreground",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        "disabled:cursor-not-allowed disabled:opacity-50",
        className
      ),
      ...props
    }
  )
);
Textarea.displayName = "Textarea";
var Switch = forwardRef(
  ({ checked, onCheckedChange, disabled, id, className, ariaLabel }, ref) => /* @__PURE__ */ jsx(
    "button",
    {
      ref,
      type: "button",
      id,
      role: "switch",
      "aria-checked": checked,
      "aria-label": ariaLabel,
      disabled,
      onClick: () => !disabled && onCheckedChange?.(!checked),
      "data-state": checked ? "checked" : "unchecked",
      className: cn(
        "relative inline-flex h-6 w-10 shrink-0 cursor-pointer items-center rounded-full border border-transparent transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        "disabled:cursor-not-allowed disabled:opacity-50",
        checked ? "bg-primary" : "bg-input",
        className
      ),
      children: /* @__PURE__ */ jsx(
        "span",
        {
          "aria-hidden": true,
          className: cn(
            "pointer-events-none inline-block h-5 w-5 rounded-full bg-background shadow ring-0 transition-transform",
            checked ? "translate-x-[18px]" : "translate-x-0.5"
          )
        }
      )
    }
  )
);
Switch.displayName = "Switch";
function PinInput({
  value,
  onChange,
  onComplete,
  length = 6,
  autoFocus = true,
  disabled = false
}) {
  const refs = useRef([]);
  const digits = value.padEnd(length, " ").slice(0, length).split("");
  useEffect(() => {
    if (autoFocus) refs.current[0]?.focus();
  }, [autoFocus]);
  useEffect(() => {
    if (value.length === length && /^\d+$/.test(value) && onComplete) {
      onComplete(value);
    }
  }, [value, length, onComplete]);
  const setAt = (i, char) => {
    const arr = digits.slice();
    arr[i] = char;
    const cleaned = arr.join("").replace(/\s/g, "").slice(0, length);
    onChange(cleaned);
  };
  const handleKey = (e, i) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      if (digits[i] !== " ") {
        setAt(i, " ");
      } else if (i > 0) {
        refs.current[i - 1]?.focus();
        setAt(i - 1, " ");
      }
      return;
    }
    if (e.key === "ArrowLeft" && i > 0) {
      e.preventDefault();
      refs.current[i - 1]?.focus();
      return;
    }
    if (e.key === "ArrowRight" && i < length - 1) {
      e.preventDefault();
      refs.current[i + 1]?.focus();
      return;
    }
    if (/^\d$/.test(e.key)) {
      e.preventDefault();
      setAt(i, e.key);
      if (i < length - 1) refs.current[i + 1]?.focus();
    }
  };
  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
    if (!pasted) return;
    e.preventDefault();
    onChange(pasted);
    const focusIdx = Math.min(pasted.length, length - 1);
    refs.current[focusIdx]?.focus();
  };
  return /* @__PURE__ */ jsx("div", { className: "flex gap-2", children: Array.from({ length }).map((_, i) => /* @__PURE__ */ jsx(
    "input",
    {
      ref: (el) => {
        refs.current[i] = el;
      },
      type: "text",
      inputMode: "numeric",
      autoComplete: "one-time-code",
      maxLength: 1,
      value: digits[i] === " " ? "" : digits[i],
      onKeyDown: (e) => handleKey(e, i),
      onPaste: handlePaste,
      onFocus: (e) => e.currentTarget.select(),
      onChange: () => {
      },
      disabled,
      className: "h-14 w-12 rounded-xl border bg-card text-center font-mono text-2xl font-semibold tabular-nums shadow-sm transition focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-50",
      "aria-label": `D\xEDgito ${i + 1}`
    },
    i
  )) });
}

// src/lib/cep.ts
var CepLookupError = class extends Error {
  code;
  constructor(code, message) {
    super(message);
    this.name = "CepLookupError";
    this.code = code;
  }
};
var onlyDigits = (s) => s.replace(/\D+/g, "");
function formatCep(raw) {
  const d = onlyDigits(raw).slice(0, 8);
  if (d.length <= 5) return d;
  return `${d.slice(0, 5)}-${d.slice(5)}`;
}
function isCompleteCep(raw) {
  return onlyDigits(raw).length === 8;
}
async function fromViaCep(digits, signal) {
  const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`, { signal });
  if (!res.ok) throw new CepLookupError("network", "Falha ao consultar o CEP.");
  const data = await res.json();
  if (data.erro) throw new CepLookupError("not_found", "CEP n\xE3o encontrado.");
  return {
    cep: formatCep(data.cep ?? digits),
    logradouro: data.logradouro ?? "",
    complemento: data.complemento ?? "",
    bairro: data.bairro ?? "",
    localidade: data.localidade ?? "",
    uf: data.uf ?? ""
  };
}
async function fromBrasilApi(digits, signal) {
  const res = await fetch(`https://brasilapi.com.br/api/cep/v2/${digits}`, { signal });
  if (res.status === 404) throw new CepLookupError("not_found", "CEP n\xE3o encontrado.");
  if (!res.ok) throw new CepLookupError("network", "Falha ao consultar o CEP.");
  const data = await res.json();
  return {
    cep: formatCep(data.cep ?? digits),
    logradouro: data.street ?? "",
    complemento: "",
    bairro: data.neighborhood ?? "",
    localidade: data.city ?? "",
    uf: data.state ?? ""
  };
}
async function lookupCep(raw, signal) {
  const digits = onlyDigits(raw);
  if (digits.length !== 8) {
    throw new CepLookupError("invalid", "Digite um CEP com 8 d\xEDgitos.");
  }
  try {
    return await fromViaCep(digits, signal);
  } catch (err) {
    if (err instanceof CepLookupError && err.code === "not_found") throw err;
    if (err?.name === "AbortError") throw err;
    return await fromBrasilApi(digits, signal);
  }
}
var CepInput = forwardRef(function CepInput2({ value, onChange, onResolved, autoLookup = true, error, className, disabled, onBlur, ...rest }, ref) {
  const [loading, setLoading] = useState(false);
  const [resolved, setResolved] = useState(false);
  const [internalError, setInternalError] = useState(null);
  const abortRef = useRef(null);
  const lastQueried = useRef(null);
  const digits = value.replace(/\D+/g, "");
  const complete = digits.length === 8;
  const run = useCallback(
    async (raw) => {
      const d = raw.replace(/\D+/g, "");
      if (d.length !== 8 || d === lastQueried.current) return;
      lastQueried.current = d;
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setLoading(true);
      setInternalError(null);
      setResolved(false);
      try {
        const addr = await lookupCep(d, ctrl.signal);
        if (ctrl.signal.aborted) return;
        setResolved(true);
        onResolved?.(addr);
      } catch (err) {
        if (err?.name === "AbortError") return;
        lastQueried.current = null;
        setInternalError(
          err instanceof CepLookupError && err.code === "not_found" ? "CEP n\xE3o encontrado." : "N\xE3o foi poss\xEDvel consultar o CEP agora."
        );
      } finally {
        if (abortRef.current === ctrl) {
          setLoading(false);
          abortRef.current = null;
        }
      }
    },
    [onResolved]
  );
  useEffect(() => {
    if (!autoLookup) return;
    if (complete && digits !== lastQueried.current) {
      void run(digits);
    } else if (!complete) {
      lastQueried.current = null;
      setResolved(false);
      setInternalError(null);
    }
  }, [autoLookup, complete, digits, run]);
  useEffect(() => () => abortRef.current?.abort(), []);
  const shownError = error ?? internalError;
  return /* @__PURE__ */ jsxs("div", { children: [
    /* @__PURE__ */ jsxs("div", { className: "relative", children: [
      /* @__PURE__ */ jsx(
        Input,
        {
          ...rest,
          ref,
          value: formatCep(value),
          inputMode: "numeric",
          placeholder: rest.placeholder ?? "00000-000",
          disabled,
          "aria-invalid": shownError ? true : void 0,
          "aria-busy": loading || void 0,
          className: cn("pr-9", shownError && "border-destructive", className),
          onChange: (e) => {
            setInternalError(null);
            onChange(formatCep(e.target.value));
          },
          onBlur: (e) => {
            if (autoLookup) void run(value);
            onBlur?.(e);
          }
        }
      ),
      /* @__PURE__ */ jsx("span", { className: "pointer-events-none absolute inset-y-0 right-2.5 flex items-center", children: loading ? /* @__PURE__ */ jsx(Loader2, { className: "h-4 w-4 animate-spin text-muted-foreground", "aria-label": "Buscando CEP" }) : resolved ? /* @__PURE__ */ jsx(Check, { className: "h-4 w-4 text-success", "aria-label": "CEP encontrado" }) : /* @__PURE__ */ jsx(Search, { className: "h-4 w-4 text-muted-foreground/60" }) })
    ] }),
    shownError ? /* @__PURE__ */ jsx("span", { className: "mt-1 block text-xs text-destructive", children: shownError }) : null
  ] });
});
var TONE_STYLES = {
  success: {
    container: "border-success-solid/30 bg-success-soft/50 text-success-soft-foreground",
    icon: "text-success-solid"
  },
  warning: {
    container: "border-warning-solid/30 bg-warning-soft/50 text-warning-soft-foreground",
    icon: "text-warning-solid"
  },
  danger: {
    container: "border-destructive-solid/30 bg-destructive-soft/50 text-destructive-soft-foreground",
    icon: "text-destructive-solid"
  },
  info: {
    container: "border-info-solid/30 bg-info-soft/50 text-info-soft-foreground",
    icon: "text-info-solid"
  },
  neutral: {
    container: "border-border bg-muted/40 text-foreground",
    icon: "text-muted-foreground"
  }
};
var DEFAULT_ICON = {
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: AlertCircle,
  info: Info,
  neutral: Info
};
function Alert({
  tone = "info",
  title,
  children,
  icon,
  actions,
  className
}) {
  const styles = TONE_STYLES[tone];
  const Icon = icon === void 0 ? DEFAULT_ICON[tone] : icon;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      role: "alert",
      className: cn(
        "flex items-start gap-3 rounded-xl border p-4 text-sm",
        styles.container,
        className
      ),
      children: [
        Icon && /* @__PURE__ */ jsx(Icon, { className: cn("mt-0.5 h-4 w-4 shrink-0", styles.icon), "aria-hidden": true }),
        /* @__PURE__ */ jsxs("div", { className: "min-w-0 flex-1", children: [
          title && /* @__PURE__ */ jsx("p", { className: "font-medium leading-snug", children: title }),
          children && /* @__PURE__ */ jsx("div", { className: cn("text-xs leading-relaxed opacity-90", title && "mt-1"), children })
        ] }),
        actions && /* @__PURE__ */ jsx("div", { className: "flex shrink-0 items-center gap-2", children: actions })
      ]
    }
  );
}
function Skeleton({
  className,
  tone = "default",
  effect = "shimmer",
  ...props
}) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: cn(
        "rounded-md",
        tone === "default" ? "bg-muted" : "bg-muted/40",
        effect === "shimmer" && "mkt-shimmer",
        effect === "pulse" && "animate-pulse",
        className
      ),
      ...props
    }
  );
}
function SkeletonList({
  count,
  className,
  itemClassName,
  tone = "soft"
}) {
  return /* @__PURE__ */ jsx("div", { className: cn("space-y-2", className), children: Array.from({ length: count }).map((_, i) => /* @__PURE__ */ jsx(Skeleton, { tone, className: itemClassName }, i)) });
}
function SkeletonText({
  lines = 3,
  lineHeight = "h-3",
  className
}) {
  return /* @__PURE__ */ jsx("div", { className: cn("space-y-2", className), children: Array.from({ length: lines }).map((_, i) => {
    const isLast = i === lines - 1;
    return /* @__PURE__ */ jsx(
      Skeleton,
      {
        tone: "soft",
        className: cn(lineHeight, isLast ? "w-3/4" : "w-full")
      },
      i
    );
  }) });
}
function Stagger({ children, delay = 40, limit }) {
  const items = Children.toArray(children);
  return /* @__PURE__ */ jsx(Fragment, { children: items.map((child, i) => {
    if (!isValidElement(child)) return child;
    const shouldStagger = limit === void 0 || i < limit;
    if (!shouldStagger) return child;
    const element = child;
    const existingClassName = element.props.className ?? "";
    const existingStyle = element.props.style ?? {};
    return cloneElement(element, {
      className: `${existingClassName} mkt-fade-up`.trim(),
      style: {
        ...existingStyle,
        animationDelay: `${i * delay}ms`
      }
    });
  }) });
}
function KeyboardHint({ combo, className, inverted }) {
  const keys = typeof combo === "string" ? combo.split("+").map((s) => s.trim()) : combo;
  return /* @__PURE__ */ jsx(
    "kbd",
    {
      className: cn(
        "inline-flex select-none items-center justify-center rounded border px-1.5 py-0.5",
        "font-mono text-xs font-medium leading-none tabular-nums",
        inverted ? "border-primary-foreground/30 bg-primary-foreground/10 text-primary-foreground" : "border-border bg-muted text-muted-foreground",
        className
      ),
      "aria-hidden": "true",
      children: formatShortcut(keys)
    }
  );
}
function Tabs({
  tabs,
  active,
  onChange,
  variant = "pill",
  size = "md",
  label,
  className
}) {
  if (variant === "underline") {
    return /* @__PURE__ */ jsx("div", { role: "tablist", "aria-label": label, className: cn("flex border-b", className), children: tabs.map((t) => {
      const Icon = t.icon;
      const isActive = active === t.key;
      return /* @__PURE__ */ jsxs(
        "button",
        {
          type: "button",
          role: "tab",
          "aria-selected": isActive,
          disabled: t.disabled,
          onClick: () => onChange(t.key),
          className: cn(
            "relative flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors",
            isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground",
            t.disabled && "cursor-not-allowed opacity-50"
          ),
          children: [
            Icon && /* @__PURE__ */ jsx(Icon, { className: "h-4 w-4" }),
            t.label,
            t.badge !== void 0 && t.badge > 0 && /* @__PURE__ */ jsx(TabBadge, { active: isActive, children: t.badge }),
            isActive && /* @__PURE__ */ jsx(
              "span",
              {
                "aria-hidden": true,
                className: "absolute inset-x-0 -bottom-px h-0.5 bg-primary transition-all"
              }
            )
          ]
        },
        t.key
      );
    }) });
  }
  const pillSize = size === "sm" ? "h-7 px-2.5 text-xs" : "h-8 px-3 text-xs";
  return /* @__PURE__ */ jsx(
    "div",
    {
      role: "tablist",
      "aria-label": label,
      className: cn(
        "inline-flex flex-wrap items-center gap-0.5 rounded-full border bg-card p-0.5",
        className
      ),
      children: tabs.map((t) => {
        const Icon = t.icon;
        const isActive = active === t.key;
        return /* @__PURE__ */ jsxs(
          "button",
          {
            type: "button",
            role: "tab",
            "aria-selected": isActive,
            disabled: t.disabled,
            onClick: () => onChange(t.key),
            className: cn(
              "inline-flex items-center gap-1.5 rounded-full font-medium transition-colors",
              pillSize,
              isActive ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
              t.disabled && "cursor-not-allowed opacity-50"
            ),
            children: [
              Icon && /* @__PURE__ */ jsx(Icon, { className: "h-3.5 w-3.5" }),
              t.label,
              t.badge !== void 0 && t.badge > 0 && /* @__PURE__ */ jsx(TabBadge, { active: isActive, children: t.badge })
            ]
          },
          t.key
        );
      })
    }
  );
}
function TabBadge({ active, children }) {
  return /* @__PURE__ */ jsx(
    "span",
    {
      className: cn(
        "inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-xs font-semibold leading-none tabular-nums",
        active ? "bg-primary-foreground/20 text-primary-foreground" : "bg-muted text-muted-foreground"
      ),
      children
    }
  );
}
var TooltipProvider = TooltipPrimitive.Provider;
var Tooltip = TooltipPrimitive.Root;
var TooltipTrigger = TooltipPrimitive.Trigger;
var TooltipContent = forwardRef(({ className, sideOffset = 8, ...props }, ref) => /* @__PURE__ */ jsx(TooltipPrimitive.Portal, { children: /* @__PURE__ */ jsx(
  TooltipPrimitive.Content,
  {
    ref,
    sideOffset,
    className: cn(
      "z-50 overflow-hidden rounded-md border bg-popover px-2.5 py-1.5 text-xs text-popover-foreground shadow-md",
      "data-[state=delayed-open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=delayed-open]:fade-in-0",
      "data-[state=delayed-open]:zoom-in-95 data-[state=closed]:zoom-out-95",
      "data-[side=right]:slide-in-from-left-1 data-[side=left]:slide-in-from-right-1 data-[side=top]:slide-in-from-bottom-1 data-[side=bottom]:slide-in-from-top-1",
      className
    ),
    ...props
  }
) }));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;
var Popover = PopoverPrimitive.Root;
var PopoverTrigger = PopoverPrimitive.Trigger;
var PopoverAnchor = PopoverPrimitive.Anchor;
var PopoverContent = forwardRef(({ className, align = "center", sideOffset = 8, ...props }, ref) => /* @__PURE__ */ jsx(PopoverPrimitive.Portal, { children: /* @__PURE__ */ jsx(
  PopoverPrimitive.Content,
  {
    ref,
    align,
    sideOffset,
    className: cn(
      "z-50 w-72 rounded-xl border bg-popover p-4 text-popover-foreground shadow-lg outline-none",
      "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      "data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95",
      "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    ),
    ...props
  }
) }));
PopoverContent.displayName = PopoverPrimitive.Content.displayName;
var DropdownMenu = DropdownMenuPrimitive.Root;
var DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
var DropdownMenuPortal = DropdownMenuPrimitive.Portal;
var DropdownMenuGroup = DropdownMenuPrimitive.Group;
var DropdownMenuContent = forwardRef(({ className, sideOffset = 4, ...props }, ref) => /* @__PURE__ */ jsx(DropdownMenuPrimitive.Portal, { children: /* @__PURE__ */ jsx(
  DropdownMenuPrimitive.Content,
  {
    ref,
    sideOffset,
    className: cn(
      "z-50 min-w-[8rem] overflow-hidden rounded-xl border bg-popover p-1.5 text-popover-foreground shadow-xl ring-1 ring-border/40",
      "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
      "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    ),
    ...props
  }
) }));
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;
var DropdownMenuItem = forwardRef(({ className, inset, ...props }, ref) => /* @__PURE__ */ jsx(
  DropdownMenuPrimitive.Item,
  {
    ref,
    className: cn(
      "relative flex cursor-pointer select-none items-center rounded-md px-2 py-1.5 text-sm outline-none transition-colors",
      "focus:bg-muted focus:text-foreground",
      "data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50",
      inset && "pl-8",
      className
    ),
    ...props
  }
));
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;
var DropdownMenuLabel = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DropdownMenuPrimitive.Label,
  {
    ref,
    className: cn(
      "px-2 pb-1 pt-0.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground",
      className
    ),
    ...props
  }
));
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;
var DropdownMenuSeparator = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  DropdownMenuPrimitive.Separator,
  {
    ref,
    className: cn("my-1 h-px bg-border", className),
    ...props
  }
));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;
var SIZE_CLASSES = {
  sm: "max-w-[400px]",
  md: "max-w-[520px]",
  lg: "max-w-[640px]",
  xl: "max-w-[720px]"
};
function SlideOver({
  open,
  onClose,
  title,
  description,
  titleHidden,
  size = "md",
  headerActions,
  footer,
  children,
  className,
  bodyClassName
}) {
  return /* @__PURE__ */ jsx(DialogPrimitive.Root, { open, onOpenChange: (o) => !o && onClose(), children: /* @__PURE__ */ jsxs(DialogPrimitive.Portal, { children: [
    /* @__PURE__ */ jsx(
      DialogPrimitive.Overlay,
      {
        className: cn(
          "fixed inset-0 z-50 bg-overlay backdrop-blur-sm",
          "data-[state=open]:animate-in data-[state=open]:fade-in-0",
          "data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
          "duration-200"
        )
      }
    ),
    /* @__PURE__ */ jsxs(
      DialogPrimitive.Content,
      {
        className: cn(
          "fixed inset-y-0 right-0 z-50 flex w-full flex-col border-l bg-card shadow-2xl outline-none",
          "data-[state=open]:animate-in data-[state=open]:slide-in-from-right",
          "data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right",
          "duration-200 ease-out",
          SIZE_CLASSES[size],
          className
        ),
        children: [
          /* @__PURE__ */ jsxs("header", { className: "flex shrink-0 items-start gap-3 border-b px-5 py-4", children: [
            /* @__PURE__ */ jsxs("div", { className: cn("min-w-0 flex-1", titleHidden && "sr-only"), children: [
              /* @__PURE__ */ jsx(DialogPrimitive.Title, { className: "text-base font-semibold leading-tight tracking-tight", children: title }),
              description && /* @__PURE__ */ jsx(DialogPrimitive.Description, { className: "mt-0.5 text-xs text-muted-foreground", children: description })
            ] }),
            titleHidden && description && /* @__PURE__ */ jsx(DialogPrimitive.Description, { className: "sr-only", children: description }),
            headerActions && /* @__PURE__ */ jsx("div", { className: "flex shrink-0 items-center gap-1", children: headerActions }),
            /* @__PURE__ */ jsx(
              DialogPrimitive.Close,
              {
                "aria-label": "Fechar",
                className: "grid h-8 w-8 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                children: /* @__PURE__ */ jsx(X, { className: "h-4 w-4" })
              }
            )
          ] }),
          /* @__PURE__ */ jsx("div", { className: cn("flex-1 overflow-y-auto overscroll-contain", bodyClassName), children }),
          footer && /* @__PURE__ */ jsx("footer", { className: "flex shrink-0 items-center justify-end gap-2 border-t bg-card px-5 py-3", children: footer })
        ]
      }
    )
  ] }) });
}
var SIZE_CLASSES2 = {
  sm: "max-w-[400px]",
  md: "max-w-[480px]",
  lg: "max-w-[560px]",
  xl: "max-w-[640px]"
};
function Dialog({
  open,
  onClose,
  title,
  description,
  titleHidden,
  size = "md",
  showClose = true,
  footer,
  children,
  className,
  bodyClassName
}) {
  return /* @__PURE__ */ jsx(DialogPrimitive.Root, { open, onOpenChange: (o) => !o && onClose(), children: /* @__PURE__ */ jsxs(DialogPrimitive.Portal, { children: [
    /* @__PURE__ */ jsx(
      DialogPrimitive.Overlay,
      {
        className: cn(
          "fixed inset-0 z-50 bg-overlay backdrop-blur-sm",
          "data-[state=open]:animate-in data-[state=open]:fade-in-0",
          "data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
          "duration-200"
        )
      }
    ),
    /* @__PURE__ */ jsx("div", { className: "pointer-events-none fixed inset-0 z-50 grid place-items-center overflow-y-auto p-4", children: /* @__PURE__ */ jsxs(
      DialogPrimitive.Content,
      {
        className: cn(
          "pointer-events-auto relative flex max-h-[calc(100dvh-2rem)] w-full flex-col rounded-2xl border bg-card shadow-2xl outline-none",
          "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
          "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
          "duration-200 ease-out",
          SIZE_CLASSES2[size],
          className
        ),
        children: [
          /* @__PURE__ */ jsxs("header", { className: "flex shrink-0 items-start gap-3 px-6 pt-6", children: [
            /* @__PURE__ */ jsxs("div", { className: cn("min-w-0 flex-1", titleHidden && "sr-only"), children: [
              /* @__PURE__ */ jsx(DialogPrimitive.Title, { className: "text-lg font-semibold leading-tight tracking-tight text-foreground", children: title }),
              description && /* @__PURE__ */ jsx(DialogPrimitive.Description, { className: "mt-1 text-sm text-muted-foreground", children: description })
            ] }),
            titleHidden && description && /* @__PURE__ */ jsx(DialogPrimitive.Description, { className: "sr-only", children: description }),
            showClose && /* @__PURE__ */ jsx(
              DialogPrimitive.Close,
              {
                "aria-label": "Fechar",
                className: "-mr-1 -mt-1 grid h-8 w-8 shrink-0 place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                children: /* @__PURE__ */ jsx(X, { className: "h-4 w-4" })
              }
            )
          ] }),
          /* @__PURE__ */ jsx("div", { className: cn("flex-1 overflow-y-auto overscroll-contain px-6 py-5", bodyClassName), children }),
          footer && /* @__PURE__ */ jsx("footer", { className: "flex shrink-0 items-center justify-end gap-2 px-6 pb-6 pt-1", children: footer })
        ]
      }
    ) })
  ] }) });
}
function NavItem({ item, badge, active, collapsed, favorite }) {
  const { LinkComponent } = useMirorUI();
  const Icon = item.icon;
  if (collapsed) {
    return /* @__PURE__ */ jsx("div", { className: "flex justify-center", children: /* @__PURE__ */ jsxs(Tooltip, { children: [
      /* @__PURE__ */ jsx(TooltipTrigger, { asChild: true, children: /* @__PURE__ */ jsx(
        LinkComponent,
        {
          to: item.to,
          className: cn(
            "flex h-9 w-9 items-center justify-center rounded-md text-sm transition-colors",
            active ? "bg-sidebar-accent text-sidebar-accent-foreground" : "text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
          ),
          children: /* @__PURE__ */ jsxs("span", { className: "relative flex shrink-0 items-center justify-center", children: [
            /* @__PURE__ */ jsx(Icon, { className: "h-4 w-4" }),
            badge > 0 && /* @__PURE__ */ jsx(
              "span",
              {
                "aria-hidden": true,
                className: "absolute -right-1 -top-1 h-2 w-2 rounded-full bg-primary ring-2 ring-sidebar"
              }
            ),
            favorite?.isFavorite && badge === 0 && /* @__PURE__ */ jsx(
              "span",
              {
                "aria-hidden": true,
                className: "absolute -right-1 -top-1 h-1.5 w-1.5 rounded-full bg-amber-500 ring-2 ring-sidebar"
              }
            )
          ] })
        }
      ) }),
      /* @__PURE__ */ jsxs(TooltipContent, { side: "right", className: "flex items-center gap-2", children: [
        favorite?.isFavorite && /* @__PURE__ */ jsx(Star, { className: "h-3 w-3 fill-current text-amber-500", "aria-hidden": true }),
        /* @__PURE__ */ jsx("span", { children: item.label }),
        badge > 0 && /* @__PURE__ */ jsx("span", { className: "inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-xs font-semibold leading-none text-primary-foreground tabular-nums", children: badge > 99 ? "99+" : badge })
      ] })
    ] }) });
  }
  if (!favorite) {
    return /* @__PURE__ */ jsxs(
      LinkComponent,
      {
        to: item.to,
        className: cn(
          "flex h-9 items-center gap-3 rounded-md px-3 text-sm transition-colors",
          active ? "bg-sidebar-accent text-sidebar-accent-foreground" : "text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
        ),
        children: [
          /* @__PURE__ */ jsx(Icon, { className: "h-4 w-4 shrink-0" }),
          /* @__PURE__ */ jsx("span", { className: "flex-1 truncate", children: item.label }),
          badge > 0 && /* @__PURE__ */ jsx(
            "span",
            {
              className: cn(
                "inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-xs font-semibold leading-none tabular-nums",
                active ? "bg-sidebar text-sidebar-foreground" : "bg-primary text-primary-foreground"
              ),
              children: badge > 99 ? "99+" : badge
            }
          )
        ]
      }
    );
  }
  const starDisabled = !favorite.isFavorite && !favorite.canFavorite;
  return /* @__PURE__ */ jsxs("div", { className: "group/nav relative", children: [
    /* @__PURE__ */ jsxs(
      LinkComponent,
      {
        to: item.to,
        className: cn(
          "flex h-9 items-center gap-3 rounded-md px-3 pr-9 text-sm transition-colors",
          active ? "bg-sidebar-accent text-sidebar-accent-foreground" : "text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
        ),
        children: [
          /* @__PURE__ */ jsx(Icon, { className: "h-4 w-4 shrink-0" }),
          /* @__PURE__ */ jsx("span", { className: "flex-1 truncate", children: item.label }),
          badge > 0 && /* @__PURE__ */ jsx(
            "span",
            {
              className: cn(
                "inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-xs font-semibold leading-none tabular-nums",
                active ? "bg-sidebar text-sidebar-foreground" : "bg-primary text-primary-foreground"
              ),
              children: badge > 99 ? "99+" : badge
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsxs(Tooltip, { children: [
      /* @__PURE__ */ jsx(TooltipTrigger, { asChild: true, children: /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          "aria-label": favorite.isFavorite ? "Remover dos favoritos" : "Adicionar aos favoritos",
          "aria-pressed": favorite.isFavorite,
          disabled: starDisabled,
          onClick: (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!starDisabled) favorite.onToggle();
          },
          className: cn(
            "absolute right-1.5 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-md transition-opacity",
            "focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
            favorite.isFavorite ? "text-amber-500 opacity-100 hover:bg-sidebar-accent" : starDisabled ? "cursor-not-allowed text-muted-foreground/40 opacity-0 group-hover/nav:opacity-60" : "text-muted-foreground/60 opacity-0 hover:bg-sidebar-accent hover:text-amber-500 group-hover/nav:opacity-100"
          ),
          children: /* @__PURE__ */ jsx(
            Star,
            {
              className: cn("h-3.5 w-3.5", favorite.isFavorite && "fill-current"),
              "aria-hidden": true
            }
          )
        }
      ) }),
      /* @__PURE__ */ jsx(TooltipContent, { side: "right", children: favorite.isFavorite ? "Remover dos favoritos" : starDisabled ? favorite.limitReachedLabel ?? "Limite de favoritos atingido" : "Adicionar aos favoritos" })
    ] })
  ] });
}
var DEFAULT_FAVORITES_LIMIT = 5;
var DEFAULT_EXPANDED = ["root"];
function isSectionExpanded(key, expandedSections, defaults) {
  if (key in expandedSections) return expandedSections[key];
  return defaults.includes(key);
}
function AppShell({
  brand,
  navGroups,
  isItemActive,
  badges = {},
  collapsed,
  onToggleCollapsed,
  expandedSections,
  onToggleSection,
  defaultExpandedKeys = DEFAULT_EXPANDED,
  navFooter,
  sidebarFooter,
  header,
  toggleShortcut = "mod+\\",
  mobileBottomNavLimit = 5,
  favorites,
  children
}) {
  const { LinkComponent } = useMirorUI();
  const mirorBrand = useMirorBrand();
  const resolvedBrand = {
    icon: brand?.icon ?? (mirorBrand.Logo ? /* @__PURE__ */ jsx(mirorBrand.Logo, { className: "h-7 w-7" }) : /* @__PURE__ */ jsx(AppLogoFallback, { className: "h-7 w-7 text-foreground", alt: mirorBrand.appName })),
    title: brand?.title ?? mirorBrand.appName,
    subtitle: brand?.subtitle ?? mirorBrand.appSubtitle,
    to: brand?.to
  };
  const favoritesLimit = favorites?.limit ?? DEFAULT_FAVORITES_LIMIT;
  const favoriteSet = useMemo(
    () => new Set(favorites?.items ?? []),
    [favorites?.items]
  );
  const navLeafByTo = useMemo(() => {
    const map = /* @__PURE__ */ new Map();
    for (const g of navGroups) for (const it of g.items) map.set(it.to, it);
    return map;
  }, [navGroups]);
  const favoritesGroup = useMemo(() => {
    if (!favorites) return null;
    const items = favorites.items.map((to) => navLeafByTo.get(to)).filter((n) => !!n);
    if (items.length === 0) return null;
    return {
      key: "favorites",
      label: favorites.sectionLabel ?? "Favoritos",
      items
    };
  }, [favorites, navLeafByTo]);
  const effectiveGroups = useMemo(() => {
    if (!favoritesGroup) return navGroups;
    const idx = navGroups.findIndex((g) => g.key !== "root");
    const insertAt = idx === -1 ? navGroups.length : idx;
    return [
      ...navGroups.slice(0, insertAt),
      favoritesGroup,
      ...navGroups.slice(insertAt)
    ];
  }, [navGroups, favoritesGroup]);
  const mobileNav = useMemo(
    () => mobileBottomNavLimit > 0 ? navGroups.flatMap((g) => g.items).slice(0, mobileBottomNavLimit) : [],
    [navGroups, mobileBottomNavLimit]
  );
  const groupBadge = (g) => g.items.reduce((sum, n) => sum + (n.badgeKey ? badges[n.badgeKey] ?? 0 : 0), 0);
  useEffect(() => {
    const key = toggleShortcut.split("+").map((s) => s.trim().toLowerCase());
    const wantsMod = key.includes("mod") || key.includes("cmd") || key.includes("ctrl");
    const wantsShift = key.includes("shift");
    const wantsAlt = key.includes("alt") || key.includes("option");
    const wantedKey = key[key.length - 1];
    const onKey = (e) => {
      const isMod = e.metaKey || e.ctrlKey;
      if (wantsMod !== isMod) return;
      if (wantsShift !== e.shiftKey) return;
      if (wantsAlt !== e.altKey) return;
      if (e.key.toLowerCase() !== wantedKey) return;
      const target = e.target;
      const tag = target?.tagName;
      const editable = target?.isContentEditable;
      if (tag === "INPUT" || tag === "TEXTAREA" || editable) return;
      e.preventDefault();
      onToggleCollapsed();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [toggleShortcut, onToggleCollapsed]);
  return /* @__PURE__ */ jsx(TooltipProvider, { delayDuration: 300, children: /* @__PURE__ */ jsxs("div", { className: "flex h-full min-h-dvh bg-background", children: [
    /* @__PURE__ */ jsxs(
      "aside",
      {
        className: cn(
          "hidden md:flex md:flex-col border-r bg-sidebar text-sidebar-foreground",
          "transition-[width] duration-200 ease-out will-change-[width]",
          collapsed ? "md:w-14" : "md:w-64"
        ),
        children: [
          /* @__PURE__ */ jsx(
            "div",
            {
              className: cn(
                "flex h-14 items-center border-b px-2",
                collapsed ? "justify-center" : "gap-3"
              ),
              children: /* @__PURE__ */ jsx(BrandContent, { brand: resolvedBrand, collapsed, LinkComponent })
            }
          ),
          /* @__PURE__ */ jsxs("nav", { className: "flex-1 overflow-y-auto p-2", children: [
            effectiveGroups.map((g, idx) => {
              const isFavGroup = g.key === "favorites";
              const expanded = isFavGroup ? true : isSectionExpanded(g.key, expandedSections, defaultExpandedKeys);
              const showHeader = !collapsed && g.label !== "";
              const badge = groupBadge(g);
              return /* @__PURE__ */ jsxs(
                "div",
                {
                  className: cn(
                    g.trailingSeparator && idx > 0 && !collapsed && "mt-3 border-t pt-3",
                    g.trailingSeparator && idx > 0 && collapsed && "mt-3 border-t pt-2",
                    !g.trailingSeparator && idx > 0 && "mt-2"
                  ),
                  children: [
                    showHeader && isFavGroup && /* @__PURE__ */ jsxs(
                      "div",
                      {
                        className: "flex h-7 w-full items-center gap-1.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground/70",
                        "aria-label": g.label,
                        children: [
                          /* @__PURE__ */ jsx(
                            Star,
                            {
                              className: "h-3 w-3 shrink-0 fill-current text-amber-500/80",
                              "aria-hidden": true
                            }
                          ),
                          /* @__PURE__ */ jsx("span", { className: "flex-1 truncate text-left", children: g.label }),
                          /* @__PURE__ */ jsxs("span", { className: "text-[10px] font-medium normal-case tracking-normal text-muted-foreground/60 tabular-nums", children: [
                            g.items.length,
                            "/",
                            favoritesLimit
                          ] })
                        ]
                      }
                    ),
                    showHeader && !isFavGroup && /* @__PURE__ */ jsxs(
                      "button",
                      {
                        type: "button",
                        onClick: () => onToggleSection(g.key),
                        className: cn(
                          "group flex h-7 w-full items-center gap-1.5 rounded-md px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 transition-colors",
                          "hover:bg-sidebar-accent/50 hover:text-muted-foreground"
                        ),
                        "aria-expanded": expanded,
                        "aria-controls": `navgroup-${g.key}`,
                        children: [
                          /* @__PURE__ */ jsx(
                            ChevronDown,
                            {
                              className: cn(
                                "h-3 w-3 shrink-0 transition-transform duration-150",
                                !expanded && "-rotate-90"
                              )
                            }
                          ),
                          /* @__PURE__ */ jsx("span", { className: "flex-1 truncate text-left", children: g.label }),
                          badge > 0 && /* @__PURE__ */ jsx("span", { className: "inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary/15 px-1 text-xs font-semibold leading-none text-primary tabular-nums", children: badge > 99 ? "99+" : badge })
                        ]
                      }
                    ),
                    (!showHeader || expanded) && /* @__PURE__ */ jsx("ul", { id: `navgroup-${g.key}`, className: "mt-0.5 space-y-0.5", children: g.items.map((n) => /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsx(
                      NavItem,
                      {
                        item: n,
                        badge: n.badgeKey ? badges[n.badgeKey] ?? 0 : 0,
                        active: isItemActive(n),
                        collapsed,
                        favorite: favorites ? {
                          isFavorite: favoriteSet.has(n.to),
                          canFavorite: favoriteSet.has(n.to) || favoriteSet.size < favoritesLimit,
                          onToggle: () => {
                            const r = favorites.onToggle(n.to);
                            return r ?? true;
                          },
                          limitReachedLabel: favorites.limitReachedLabel ?? `M\xE1ximo de ${favoritesLimit} favoritos`
                        } : void 0
                      }
                    ) }, `${g.key}::${n.to}`)) })
                  ]
                },
                g.key
              );
            }),
            navFooter && (typeof navFooter === "function" ? navFooter({ collapsed }) : navFooter)
          ] }),
          typeof sidebarFooter === "function" ? sidebarFooter({ collapsed, onToggleCollapsed }) : /* @__PURE__ */ jsxs("div", { className: "border-t p-2", children: [
            sidebarFooter && /* @__PURE__ */ jsx("div", { className: cn("mb-1", collapsed && "flex flex-col items-center gap-1"), children: sidebarFooter }),
            /* @__PURE__ */ jsx("div", { className: cn("flex items-center", collapsed ? "flex-col gap-1" : "justify-end"), children: /* @__PURE__ */ jsxs(Tooltip, { children: [
              /* @__PURE__ */ jsx(TooltipTrigger, { asChild: true, children: /* @__PURE__ */ jsx(
                "button",
                {
                  type: "button",
                  "aria-label": collapsed ? "Expandir menu" : "Recolher menu",
                  onClick: onToggleCollapsed,
                  className: "grid h-8 w-8 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                  children: collapsed ? /* @__PURE__ */ jsx(PanelLeft, { className: "h-4 w-4" }) : /* @__PURE__ */ jsx(PanelLeftClose, { className: "h-4 w-4" })
                }
              ) }),
              /* @__PURE__ */ jsxs(
                TooltipContent,
                {
                  side: collapsed ? "right" : "top",
                  className: "flex items-center gap-2",
                  children: [
                    /* @__PURE__ */ jsx("span", { children: collapsed ? "Expandir menu" : "Recolher menu" }),
                    /* @__PURE__ */ jsx(KeyboardHint, { combo: toggleShortcut })
                  ]
                }
              )
            ] }) })
          ] })
        ]
      }
    ),
    /* @__PURE__ */ jsxs("div", { className: "flex flex-1 flex-col min-w-0", children: [
      header && /* @__PURE__ */ jsx("header", { className: "sticky top-0 z-30 flex h-14 items-center gap-4 border-b bg-background/85 px-4 backdrop-blur sm:px-6", children: header }),
      /* @__PURE__ */ jsx("main", { className: "flex-1 overflow-y-auto pb-20 md:pb-4", children }),
      mobileNav.length > 0 && /* @__PURE__ */ jsx("nav", { className: "fixed inset-x-0 bottom-0 z-40 flex items-center justify-around border-t bg-background/95 backdrop-blur md:hidden", children: mobileNav.map((item) => {
        const Icon = item.icon;
        const active = isItemActive(item);
        return /* @__PURE__ */ jsxs(
          LinkComponent,
          {
            to: item.to,
            className: cn(
              "flex flex-1 flex-col items-center gap-1 py-2 text-xs",
              active ? "text-primary" : "text-muted-foreground"
            ),
            children: [
              /* @__PURE__ */ jsx(Icon, { className: "h-5 w-5" }),
              /* @__PURE__ */ jsx("span", { children: item.label })
            ]
          },
          item.to
        );
      }) })
    ] })
  ] }) });
}
function BrandContent({
  brand,
  collapsed,
  LinkComponent
}) {
  const inner = /* @__PURE__ */ jsxs(Fragment, { children: [
    brand.icon && /* @__PURE__ */ jsx("span", { className: "flex h-7 w-7 shrink-0 items-center justify-center", children: brand.icon }),
    !collapsed && /* @__PURE__ */ jsxs("div", { className: "flex min-w-0 flex-col leading-tight", children: [
      /* @__PURE__ */ jsx("span", { className: "text-sm font-semibold", children: brand.title }),
      brand.subtitle && /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground tabular-nums", children: brand.subtitle })
    ] })
  ] });
  if (brand.to) {
    return /* @__PURE__ */ jsx(LinkComponent, { to: brand.to, className: "flex items-center gap-3 truncate", children: inner });
  }
  return /* @__PURE__ */ jsx(Fragment, { children: inner });
}
function Breadcrumb({
  items,
  hideHome,
  homeLabel = "In\xEDcio",
  homeTo = "/",
  size = "sm",
  truncate,
  className
}) {
  const { LinkComponent } = useMirorUI();
  const trail = [];
  if (!hideHome) trail.push({ label: homeLabel, to: homeTo });
  trail.push(...items);
  if (trail.length === 0) return null;
  return /* @__PURE__ */ jsx(
    "nav",
    {
      "aria-label": "Breadcrumb",
      className: cn(
        "flex items-center gap-1 text-muted-foreground",
        size === "sm" ? "text-xs" : "text-sm",
        truncate && "min-w-0 max-w-full",
        className
      ),
      children: trail.map((item, i) => {
        const isLast = i === trail.length - 1;
        const chevronSize = size === "sm" ? "h-3 w-3" : "h-3.5 w-3.5";
        return /* @__PURE__ */ jsxs(
          "span",
          {
            className: cn(
              "inline-flex items-center gap-1",
              truncate && !isLast && "shrink min-w-0",
              truncate && isLast && "min-w-0 shrink"
            ),
            children: [
              item.to && !isLast ? /* @__PURE__ */ jsx(
                LinkComponent,
                {
                  to: item.to,
                  className: cn(
                    "transition-colors hover:text-foreground",
                    truncate && "truncate"
                  ),
                  children: item.label
                }
              ) : /* @__PURE__ */ jsx(
                "span",
                {
                  className: cn(
                    isLast && "font-medium text-foreground",
                    truncate && "truncate"
                  ),
                  children: item.label
                }
              ),
              !isLast && /* @__PURE__ */ jsx(ChevronRight, { className: cn("shrink-0", chevronSize), "aria-hidden": true })
            ]
          },
          `${item.label}-${i}`
        );
      })
    }
  );
}
function AppHeaderBar({
  title,
  breadcrumb,
  homeLabel = "In\xEDcio",
  homeTo = "/",
  hideHome,
  hideMobileBrand,
  actions,
  className
}) {
  const brand = useMirorBrand();
  const meta = useMirorPageMeta();
  const resolvedTitle = title ?? meta.title ?? void 0;
  const resolvedBreadcrumb = breadcrumb ?? meta.breadcrumb;
  const ancestors = resolvedBreadcrumb.slice(0, -1);
  const showBreadcrumb = ancestors.length > 0;
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-1 items-center gap-4", className), children: [
    !hideMobileBrand && /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 md:hidden", children: [
      brand.Logo ? /* @__PURE__ */ jsx(brand.Logo, { className: "h-9 w-9" }) : /* @__PURE__ */ jsx(AppLogoFallback, { className: "h-9 w-9 text-foreground", alt: brand.appName }),
      /* @__PURE__ */ jsx("span", { className: "text-sm font-semibold leading-tight", children: brand.appName }),
      brand.appVersion && /* @__PURE__ */ jsxs("span", { className: "text-xs text-muted-foreground tabular-nums", children: [
        "v",
        brand.appVersion
      ] })
    ] }),
    resolvedTitle && /* @__PURE__ */ jsxs("div", { className: "hidden min-w-0 flex-1 md:flex md:flex-col md:justify-center md:leading-tight", children: [
      /* @__PURE__ */ jsx("p", { className: "truncate text-sm font-semibold text-foreground", children: resolvedTitle }),
      showBreadcrumb && /* @__PURE__ */ jsx(
        Breadcrumb,
        {
          items: ancestors,
          hideHome,
          homeLabel,
          homeTo,
          size: "sm",
          truncate: true
        }
      )
    ] }),
    !resolvedTitle && /* @__PURE__ */ jsx("div", { className: "flex-1" }),
    actions && /* @__PURE__ */ jsx("div", { className: "flex shrink-0 items-center gap-2", children: actions })
  ] });
}
function ThemeToggleButton({
  store,
  className,
  noTooltip,
  tooltipSide = "top"
}) {
  const [isDark, setIsDark] = useState(
    () => typeof document !== "undefined" && document.documentElement.classList.contains("dark")
  );
  useEffect(() => {
    if (typeof document === "undefined") return;
    const obs = new MutationObserver(
      () => setIsDark(document.documentElement.classList.contains("dark"))
    );
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);
  const Icon = isDark ? Sun : Moon;
  const label = isDark ? "Modo claro" : "Modo escuro";
  const button = /* @__PURE__ */ jsx(
    "button",
    {
      type: "button",
      "aria-label": label,
      onClick: () => store.set(isDark ? "light" : "dark"),
      className: cn(
        "grid h-8 w-8 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
        className
      ),
      children: /* @__PURE__ */ jsx(Icon, { className: "h-4 w-4" })
    }
  );
  if (noTooltip) return button;
  return /* @__PURE__ */ jsxs(Tooltip, { delayDuration: 300, children: [
    /* @__PURE__ */ jsx(TooltipTrigger, { asChild: true, children: button }),
    /* @__PURE__ */ jsx(TooltipContent, { side: tooltipSide, children: label })
  ] });
}
function SidebarUserFooter({
  userEmail,
  onLogout,
  themeStore,
  collapsed,
  onToggleCollapsed,
  toggleShortcut = "mod+\\"
}) {
  return /* @__PURE__ */ jsxs("div", { className: "border-t p-2", children: [
    !collapsed && userEmail && /* @__PURE__ */ jsx(
      "div",
      {
        className: "mb-2 px-2 text-xs text-muted-foreground truncate",
        title: userEmail,
        children: userEmail
      }
    ),
    /* @__PURE__ */ jsxs(
      "div",
      {
        className: cn(
          "flex items-center gap-1",
          collapsed ? "flex-col" : "justify-between"
        ),
        children: [
          themeStore && /* @__PURE__ */ jsx(ThemeToggleButton, { store: themeStore }),
          onLogout && /* @__PURE__ */ jsxs(Tooltip, { children: [
            /* @__PURE__ */ jsx(TooltipTrigger, { asChild: true, children: /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                "aria-label": "Sair",
                onClick: onLogout,
                className: "grid h-8 w-8 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                children: /* @__PURE__ */ jsx(LogOut, { className: "h-4 w-4" })
              }
            ) }),
            /* @__PURE__ */ jsx(TooltipContent, { side: collapsed ? "right" : "top", children: "Sair" })
          ] }),
          /* @__PURE__ */ jsxs(Tooltip, { children: [
            /* @__PURE__ */ jsx(TooltipTrigger, { asChild: true, children: /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                "aria-label": collapsed ? "Expandir menu" : "Recolher menu",
                onClick: onToggleCollapsed,
                className: "grid h-8 w-8 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                children: collapsed ? /* @__PURE__ */ jsx(PanelLeft, { className: "h-4 w-4" }) : /* @__PURE__ */ jsx(PanelLeftClose, { className: "h-4 w-4" })
              }
            ) }),
            /* @__PURE__ */ jsxs(
              TooltipContent,
              {
                side: collapsed ? "right" : "top",
                className: "flex items-center gap-2",
                children: [
                  /* @__PURE__ */ jsx("span", { children: collapsed ? "Expandir menu" : "Recolher menu" }),
                  /* @__PURE__ */ jsx(KeyboardHint, { combo: toggleShortcut })
                ]
              }
            )
          ] })
        ]
      }
    )
  ] });
}
function SidebarAppsSection({
  items,
  onLaunch,
  collapsed,
  label = "Apps da su\xEDte",
  hideHeaderIcon
}) {
  if (items.length === 0) return null;
  return /* @__PURE__ */ jsxs("div", { className: "mt-3 border-t pt-3", children: [
    !collapsed && /* @__PURE__ */ jsxs("div", { className: "flex h-7 w-full items-center gap-1.5 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground/70", children: [
      !hideHeaderIcon && /* @__PURE__ */ jsx(AppWindow, { className: "h-3 w-3 shrink-0", "aria-hidden": true }),
      /* @__PURE__ */ jsx("span", { className: "flex-1 truncate text-left", children: label })
    ] }),
    /* @__PURE__ */ jsx("ul", { className: "mt-0.5 space-y-0.5", children: items.map((app) => {
      const Icon = app.icon;
      if (collapsed) {
        return /* @__PURE__ */ jsx("li", { className: "flex justify-center", children: /* @__PURE__ */ jsxs(Tooltip, { children: [
          /* @__PURE__ */ jsx(TooltipTrigger, { asChild: true, children: /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              onClick: () => onLaunch(app),
              className: "flex h-9 w-9 items-center justify-center rounded-md text-sm text-muted-foreground transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
              children: /* @__PURE__ */ jsx(Icon, { className: "h-4 w-4" })
            }
          ) }),
          /* @__PURE__ */ jsxs(TooltipContent, { side: "right", className: "flex items-center gap-2", children: [
            /* @__PURE__ */ jsx("span", { children: app.name }),
            /* @__PURE__ */ jsx(ExternalLink, { className: "h-3 w-3 text-muted-foreground" })
          ] })
        ] }) }, app.key);
      }
      return /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsxs(
        "button",
        {
          type: "button",
          onClick: () => onLaunch(app),
          className: cn(
            "flex h-9 w-full items-center gap-3 rounded-md px-3 text-sm transition-colors",
            "text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
          ),
          children: [
            /* @__PURE__ */ jsx(Icon, { className: "h-4 w-4 shrink-0" }),
            /* @__PURE__ */ jsx("span", { className: "flex-1 truncate text-left", children: app.name }),
            /* @__PURE__ */ jsx(
              ExternalLink,
              {
                className: "h-3.5 w-3.5 shrink-0 opacity-50",
                "aria-label": "Abre em nova aba"
              }
            )
          ]
        }
      ) }, app.key);
    }) })
  ] });
}
function CompanySwitcher({
  companies,
  activeId,
  onChange,
  ariaLabel = "Empresa ativa",
  className
}) {
  if (!companies || companies.length === 0) return null;
  return /* @__PURE__ */ jsxs("div", { className: cn("relative inline-flex items-center", className), children: [
    /* @__PURE__ */ jsx(Building2, { className: "absolute left-2 h-4 w-4 text-muted-foreground pointer-events-none" }),
    /* @__PURE__ */ jsx(
      "select",
      {
        "aria-label": ariaLabel,
        value: activeId ?? "",
        onChange: (e) => onChange(e.target.value || null),
        className: "appearance-none rounded-md border bg-background py-1.5 pl-8 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-ring",
        children: companies.map((c) => /* @__PURE__ */ jsx("option", { value: c.id, children: c.name }, c.id))
      }
    ),
    /* @__PURE__ */ jsx(ChevronsUpDown, { className: "absolute right-2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" })
  ] });
}
var DEFAULT_PLACEHOLDER = "Digite para buscar p\xE1ginas, a\xE7\xF5es\u2026";
var DEFAULT_EMPTY = (q) => q.trim() ? `Nenhum resultado para "${q.trim()}".` : "Sem comandos dispon\xEDveis.";
var DEFAULT_LIMIT = 50;
function normalize(s) {
  return s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
}
function matchesQuery(haystack, query) {
  if (!query.trim()) return 1;
  const q = normalize(query.trim());
  const h = normalize(haystack);
  if (h.includes(q)) return h.startsWith(q) ? 3 : 2;
  let i = 0;
  for (const ch of h) {
    if (ch === q[i]) i++;
    if (i === q.length) return 1;
  }
  return 0;
}
function CommandPalette({
  open,
  onOpenChange,
  commands,
  placeholder = DEFAULT_PLACEHOLDER,
  emptyLabel = DEFAULT_EMPTY,
  shortcut = "mod+k",
  resultLimit = DEFAULT_LIMIT
}) {
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  useEffect(() => {
    if (!shortcut) return;
    const parts = shortcut.split("+").map((s) => s.trim().toLowerCase());
    const wantsMod = parts.includes("mod") || parts.includes("cmd") || parts.includes("ctrl");
    const wantsShift = parts.includes("shift");
    const wantsAlt = parts.includes("alt") || parts.includes("option");
    const wantedKey = parts[parts.length - 1];
    const onKey = (e) => {
      const isMod = e.metaKey || e.ctrlKey;
      if (wantsMod !== isMod) return;
      if (wantsShift !== e.shiftKey) return;
      if (wantsAlt !== e.altKey) return;
      if (e.key.toLowerCase() !== wantedKey) return;
      e.preventDefault();
      onOpenChange(!open);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [shortcut, onOpenChange, open]);
  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIndex(0);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);
  const rawCommands = useMemo(
    () => typeof commands === "function" ? commands(query) : commands,
    [commands, query]
  );
  const filtered = useMemo(() => {
    const q = query.trim();
    const scored = rawCommands.map((c) => {
      const haystack = [c.label, c.description ?? "", c.keywords ?? ""].join(" ");
      return { c, s: matchesQuery(haystack, q) };
    }).filter((x) => x.s > 0);
    scored.sort((a, b) => {
      if (b.s !== a.s) return b.s - a.s;
      return a.c.label.localeCompare(b.c.label, "pt");
    });
    return scored.slice(0, resultLimit).map((x) => x.c);
  }, [rawCommands, query, resultLimit]);
  const sections = useMemo(() => {
    const map = /* @__PURE__ */ new Map();
    for (const c of filtered) {
      const arr = map.get(c.group) ?? [];
      arr.push(c);
      map.set(c.group, arr);
    }
    return Array.from(map.entries());
  }, [filtered]);
  const close = useCallback(() => onOpenChange(false), [onOpenChange]);
  const runActive = useCallback(() => {
    const cmd = filtered[activeIndex];
    if (cmd) {
      cmd.run();
      close();
    }
  }, [activeIndex, filtered, close]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        close();
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        runActive();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, filtered.length, close, runActive]);
  useLayoutEffect(() => {
    setActiveIndex(0);
  }, [query]);
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector(
      `[data-cmd-index="${activeIndex}"]`
    );
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [activeIndex, open]);
  if (!open) return null;
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: "fixed inset-0 z-[100] flex items-start justify-center bg-black/40 p-4 pt-[12vh] backdrop-blur-sm dark:bg-black/60",
      onClick: (e) => {
        if (e.target === e.currentTarget) close();
      },
      children: /* @__PURE__ */ jsxs(
        "div",
        {
          className: "w-full max-w-xl overflow-hidden rounded-2xl border bg-card shadow-2xl",
          role: "dialog",
          "aria-modal": "true",
          children: [
            /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 border-b px-4 py-3", children: [
              /* @__PURE__ */ jsx(Search, { className: "h-4 w-4 shrink-0 text-muted-foreground" }),
              /* @__PURE__ */ jsx(
                "input",
                {
                  ref: inputRef,
                  value: query,
                  onChange: (e) => setQuery(e.target.value),
                  placeholder,
                  className: "flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                }
              ),
              /* @__PURE__ */ jsx("kbd", { className: "hidden rounded bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground sm:inline", children: "esc" })
            ] }),
            /* @__PURE__ */ jsx("div", { ref: listRef, className: "max-h-[60vh] overflow-y-auto p-2", children: filtered.length === 0 ? /* @__PURE__ */ jsx("div", { className: "px-3 py-10 text-center text-sm text-muted-foreground", children: emptyLabel(query) }) : sections.map(([groupName, groupCmds]) => /* @__PURE__ */ jsxs("div", { className: "mb-1", children: [
              /* @__PURE__ */ jsx("p", { className: "px-2 pb-1 pt-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground/80", children: groupName }),
              groupCmds.map((c) => {
                const globalIndex = filtered.indexOf(c);
                const isActive = globalIndex === activeIndex;
                const Icon = c.icon;
                return /* @__PURE__ */ jsxs(
                  "button",
                  {
                    type: "button",
                    "data-cmd-index": globalIndex,
                    onMouseMove: () => setActiveIndex(globalIndex),
                    onClick: () => {
                      c.run();
                      close();
                    },
                    className: cn(
                      "flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left transition-colors",
                      isActive ? "bg-primary/10 text-foreground" : "text-foreground/80 hover:bg-muted/40"
                    ),
                    children: [
                      /* @__PURE__ */ jsx(Icon, { className: "h-4 w-4 shrink-0 text-muted-foreground" }),
                      /* @__PURE__ */ jsxs("div", { className: "min-w-0 flex-1", children: [
                        /* @__PURE__ */ jsx("p", { className: "truncate text-sm", children: c.label }),
                        c.description && /* @__PURE__ */ jsx("p", { className: "truncate text-xs text-muted-foreground", children: c.description })
                      ] }),
                      isActive && /* @__PURE__ */ jsx(CornerDownLeft, { className: "h-3 w-3 shrink-0 text-muted-foreground/60" })
                    ]
                  },
                  c.id
                );
              })
            ] }, groupName)) }),
            /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-3 border-t bg-muted/20 px-4 py-2 text-xs text-muted-foreground", children: [
              /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3", children: [
                /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1", children: [
                  /* @__PURE__ */ jsx("kbd", { className: "rounded bg-card px-1 py-0.5 font-medium", children: "\u2191" }),
                  /* @__PURE__ */ jsx("kbd", { className: "rounded bg-card px-1 py-0.5 font-medium", children: "\u2193" }),
                  "navegar"
                ] }),
                /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1", children: [
                  /* @__PURE__ */ jsx("kbd", { className: "rounded bg-card px-1 py-0.5 font-medium", children: "\u21B5" }),
                  "executar"
                ] })
              ] }),
              /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1", children: [
                /* @__PURE__ */ jsx(ArrowRight, { className: "h-3 w-3" }),
                filtered.length,
                " resultado",
                filtered.length === 1 ? "" : "s"
              ] })
            ] })
          ]
        }
      )
    }
  );
}
function TourHelpButton({
  tourId,
  onOpen,
  className,
  ariaLabel = "Abrir guia desta tela",
  tooltip = "Guia desta tela (?)"
}) {
  if (!tourId) return null;
  return /* @__PURE__ */ jsxs(Tooltip, { children: [
    /* @__PURE__ */ jsx(TooltipTrigger, { asChild: true, children: /* @__PURE__ */ jsx(
      Button,
      {
        variant: "ghost",
        size: "icon",
        className: cn(className),
        "aria-label": ariaLabel,
        onClick: () => onOpen(tourId),
        children: /* @__PURE__ */ jsx(HelpCircle, { className: "h-4 w-4" })
      }
    ) }),
    /* @__PURE__ */ jsx(TooltipContent, { side: "bottom", children: tooltip })
  ] });
}
function UserMenu({
  user,
  onLogout,
  onOpenSettings,
  items,
  ariaLabel = "Menu do usu\xE1rio",
  header,
  settingsLabel = "Configura\xE7\xF5es",
  logoutLabel = "Sair"
}) {
  const initial = (user?.name?.trim()?.slice(0, 1) ?? "?").toUpperCase();
  const showHeader = !!header || !!user;
  return /* @__PURE__ */ jsxs(DropdownMenu, { children: [
    /* @__PURE__ */ jsx(DropdownMenuTrigger, { asChild: true, children: /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        "aria-label": ariaLabel,
        className: "grid h-9 w-9 place-items-center rounded-full bg-muted text-sm font-semibold text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        children: user?.avatarUrl ? /* @__PURE__ */ jsx(
          "img",
          {
            src: user.avatarUrl,
            alt: "",
            className: "h-9 w-9 rounded-full object-cover",
            onError: (e) => {
              e.currentTarget.style.display = "none";
            }
          }
        ) : initial
      }
    ) }),
    /* @__PURE__ */ jsxs(DropdownMenuContent, { align: "end", className: "w-60", children: [
      showHeader && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(DropdownMenuLabel, { className: "normal-case tracking-normal", children: header ?? /* @__PURE__ */ jsxs("div", { className: "space-y-0.5", children: [
          user?.name && /* @__PURE__ */ jsx("p", { className: "text-sm font-medium text-foreground", children: user.name }),
          user?.email && /* @__PURE__ */ jsx("p", { className: "truncate text-xs text-muted-foreground", children: user.email })
        ] }) }),
        /* @__PURE__ */ jsx(DropdownMenuSeparator, {})
      ] }),
      onOpenSettings && /* @__PURE__ */ jsxs(DropdownMenuItem, { onClick: onOpenSettings, children: [
        /* @__PURE__ */ jsx(Settings, { className: "mr-2 h-4 w-4" }),
        settingsLabel
      ] }),
      items?.map((it, i) => {
        const Icon = it.icon;
        return /* @__PURE__ */ jsxs(
          DropdownMenuItem,
          {
            onClick: it.onClick,
            className: it.destructive ? "text-destructive focus:text-destructive" : void 0,
            children: [
              Icon && /* @__PURE__ */ jsx(Icon, { className: "mr-2 h-4 w-4" }),
              it.label
            ]
          },
          `${it.label}-${i}`
        );
      }),
      onLogout && /* @__PURE__ */ jsxs(Fragment, { children: [
        (onOpenSettings || items && items.length > 0) && /* @__PURE__ */ jsx(DropdownMenuSeparator, {}),
        /* @__PURE__ */ jsxs(
          DropdownMenuItem,
          {
            onClick: onLogout,
            className: "text-destructive focus:text-destructive",
            children: [
              /* @__PURE__ */ jsx(LogOut, { className: "mr-2 h-4 w-4" }),
              logoutLabel
            ]
          }
        )
      ] })
    ] })
  ] });
}
function SessionGate({
  title = "Verificando sess\xE3o",
  subtitle = "Conectando ao seu workspace",
  srLabel
}) {
  return /* @__PURE__ */ jsxs("main", { className: "sg-root", role: "status", "aria-live": "polite", "aria-busy": "true", children: [
    /* @__PURE__ */ jsx("div", { className: "sg-aurora", "aria-hidden": "true" }),
    /* @__PURE__ */ jsx("div", { className: "sg-spotlight", "aria-hidden": "true" }),
    /* @__PURE__ */ jsx("div", { className: "sg-grain", "aria-hidden": "true" }),
    /* @__PURE__ */ jsxs("div", { className: "sg-stage", children: [
      /* @__PURE__ */ jsx("div", { className: "sg-mark", children: /* @__PURE__ */ jsxs(
        "svg",
        {
          className: "sg-svg",
          viewBox: "0 0 418 451",
          xmlns: "http://www.w3.org/2000/svg",
          "aria-hidden": "true",
          children: [
            /* @__PURE__ */ jsx(
              "path",
              {
                className: "sg-arc sg-arc--1",
                d: "M 62.33 316.23 A 170 170 0 0 1 305.8 91.56"
              }
            ),
            /* @__PURE__ */ jsx(
              "path",
              {
                className: "sg-arc sg-arc--2",
                d: "M 357.67 147.77 A 170 170 0 0 1 114.2 372.44"
              }
            ),
            /* @__PURE__ */ jsx("line", { className: "sg-beam", x1: "39", y1: "389", x2: "380", y2: "74" })
          ]
        }
      ) }),
      /* @__PURE__ */ jsxs("div", { className: "sg-copy", children: [
        /* @__PURE__ */ jsx("h1", { className: "sg-title", children: title }),
        /* @__PURE__ */ jsxs("p", { className: "sg-subtitle", children: [
          /* @__PURE__ */ jsx("span", { children: subtitle }),
          /* @__PURE__ */ jsxs("span", { className: "sg-dots", "aria-hidden": "true", children: [
            /* @__PURE__ */ jsx("i", {}),
            /* @__PURE__ */ jsx("i", {}),
            /* @__PURE__ */ jsx("i", {})
          ] })
        ] })
      ] }),
      /* @__PURE__ */ jsx("div", { className: "sg-track", "aria-hidden": "true" })
    ] }),
    /* @__PURE__ */ jsx("span", { className: "sg-sr-only", children: srLabel ?? `${title}. ${subtitle}.` })
  ] });
}
function useMinimumDuration(ms) {
  const [elapsed, setElapsed] = useState(false);
  useEffect(() => {
    if (ms <= 0) {
      setElapsed(true);
      return;
    }
    const t = window.setTimeout(() => setElapsed(true), ms);
    return () => window.clearTimeout(t);
  }, [ms]);
  return elapsed;
}
function PageHeader({
  breadcrumb,
  hideHome,
  homeLabel = "In\xEDcio",
  homeTo = "/",
  title,
  description,
  actions,
  children,
  className,
  noAutoMeta
}) {
  useSetPageMeta(
    noAutoMeta ? { title: void 0, breadcrumb: void 0, description: void 0 } : { title, breadcrumb: breadcrumb ?? [], description: description ?? null }
  );
  const hasProvider = useHasPageMetaProvider();
  const isSyncing = !noAutoMeta && hasProvider;
  const showLocalBreadcrumb = breadcrumb && breadcrumb.length > 0 && !isSyncing;
  const showTitleBlock = !isSyncing;
  const hasActions = !!actions;
  const hasChildren = !!children;
  if (!showLocalBreadcrumb && !showTitleBlock && !hasActions && !hasChildren) {
    return null;
  }
  return /* @__PURE__ */ jsxs("header", { className: cn("mb-5 sm:mb-7", className), children: [
    showLocalBreadcrumb && /* @__PURE__ */ jsx(
      Breadcrumb,
      {
        items: breadcrumb,
        hideHome,
        homeLabel,
        homeTo,
        size: "sm",
        className: "mb-2"
      }
    ),
    (showTitleBlock || hasActions) && /* @__PURE__ */ jsxs(
      "div",
      {
        className: cn(
          "flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4",
          showTitleBlock ? "sm:justify-between" : "sm:justify-end"
        ),
        children: [
          showTitleBlock && /* @__PURE__ */ jsxs("div", { className: "min-w-0 flex-1", children: [
            /* @__PURE__ */ jsx("h1", { className: "font-heading text-2xl font-semibold tracking-tight text-foreground", children: title }),
            description && /* @__PURE__ */ jsx("p", { className: "mt-1.5 text-sm text-muted-foreground", children: description })
          ] }),
          hasActions && /* @__PURE__ */ jsx("div", { className: "flex flex-wrap items-center gap-2", children: actions })
        ]
      }
    ),
    hasChildren && /* @__PURE__ */ jsx("div", { className: cn(showTitleBlock || hasActions ? "mt-5" : "mt-0"), children })
  ] });
}
function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  secondaryAction,
  variant = "card",
  size = "md",
  className
}) {
  const padding = size === "sm" ? "py-8" : size === "lg" ? "py-20" : "py-14";
  const inner = /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col items-center text-center", padding), children: [
    Icon && /* @__PURE__ */ jsx("div", { className: "mb-4 grid h-14 w-14 place-items-center rounded-full bg-muted", children: /* @__PURE__ */ jsx(Icon, { className: "h-7 w-7 text-muted-foreground" }) }),
    /* @__PURE__ */ jsx("h3", { className: "text-base font-semibold", children: title }),
    description && /* @__PURE__ */ jsx("p", { className: "mt-1.5 max-w-md text-sm text-muted-foreground", children: description }),
    (action || secondaryAction) && /* @__PURE__ */ jsxs("div", { className: "mt-5 flex flex-wrap items-center justify-center gap-2", children: [
      action && /* @__PURE__ */ jsx(ActionButton, { ...action }),
      secondaryAction && /* @__PURE__ */ jsx(ActionButton, { variant: "outline", ...secondaryAction })
    ] })
  ] });
  if (variant === "flat") {
    return /* @__PURE__ */ jsx("div", { className: cn("w-full", className), children: inner });
  }
  return /* @__PURE__ */ jsx("div", { className: cn("w-full rounded-xl border border-dashed bg-card/40", className), children: inner });
}
function ActionButton({ label, onClick, to, variant = "default" }) {
  const { LinkComponent } = useMirorUI();
  if (to) {
    return /* @__PURE__ */ jsx(Button, { asChild: true, variant, children: /* @__PURE__ */ jsx(LinkComponent, { to, children: label }) });
  }
  return /* @__PURE__ */ jsx(Button, { variant, onClick, children: label });
}
function FilteredEmpty({
  onClear,
  title = "Nada encontrado",
  description = "Tente ajustar os filtros ou limpar a busca para ver mais resultados.",
  variant = "card",
  size = "md",
  className
}) {
  return /* @__PURE__ */ jsx(
    EmptyState,
    {
      icon: SearchX,
      title,
      description,
      action: { label: "Limpar filtros", onClick: onClear, variant: "outline" },
      variant,
      size,
      className
    }
  );
}
function MetricCard({
  label,
  value,
  delta,
  deltaSuffix = "%",
  hint,
  icon: Icon,
  to,
  loading,
  className
}) {
  const { LinkComponent } = useMirorUI();
  const inner = /* @__PURE__ */ jsxs("div", { className: "flex h-full flex-col gap-3 p-4 sm:p-5", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between gap-2", children: [
      /* @__PURE__ */ jsx("span", { className: "truncate text-xs font-medium uppercase tracking-wider text-muted-foreground", children: label }),
      Icon && /* @__PURE__ */ jsx(Icon, { className: "h-4 w-4 shrink-0 text-muted-foreground" })
    ] }),
    loading ? /* @__PURE__ */ jsxs("div", { className: "space-y-2", children: [
      /* @__PURE__ */ jsx(Skeleton, { className: "h-7 w-24" }),
      /* @__PURE__ */ jsx(Skeleton, { className: "h-3 w-32", tone: "soft" })
    ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx("div", { className: "text-2xl font-semibold tabular-nums tracking-tight sm:text-3xl", children: value }),
      (delta !== void 0 || hint) && /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1.5 text-xs text-muted-foreground", children: [
        delta !== void 0 && /* @__PURE__ */ jsx(DeltaPill, { delta, suffix: deltaSuffix }),
        hint && /* @__PURE__ */ jsx("span", { className: "truncate", children: hint })
      ] })
    ] })
  ] });
  const wrapper = cn(
    "rounded-xl border bg-card text-card-foreground shadow-sm transition-[box-shadow,transform] duration-200",
    to && "hover:-translate-y-px hover:shadow-md",
    className
  );
  if (to) {
    return /* @__PURE__ */ jsx(LinkComponent, { to, className: wrapper, children: inner });
  }
  return /* @__PURE__ */ jsx("div", { className: wrapper, children: inner });
}
function DeltaPill({ delta, suffix }) {
  const positive = delta > 0;
  const negative = delta < 0;
  const Icon = positive ? ArrowUpRight : negative ? ArrowDownRight : ArrowRight;
  return /* @__PURE__ */ jsxs(
    "span",
    {
      className: cn(
        "inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-xs font-semibold tabular-nums",
        positive && "bg-success-soft text-success-soft-foreground",
        negative && "bg-destructive-soft text-destructive-soft-foreground",
        !positive && !negative && "bg-muted text-muted-foreground"
      ),
      children: [
        /* @__PURE__ */ jsx(Icon, { className: "h-3 w-3" }),
        positive ? "+" : "",
        delta,
        suffix
      ]
    }
  );
}
function FilterBar({
  search,
  onSearchChange,
  searchPlaceholder = "Buscar\u2026",
  chips,
  activeChip,
  onChipChange,
  trailing,
  className
}) {
  const showSearch = onSearchChange !== void 0;
  const showChips = chips && chips.length > 0;
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4", className), children: [
    showSearch && /* @__PURE__ */ jsxs("div", { className: "relative w-full sm:w-72 shrink-0", children: [
      /* @__PURE__ */ jsx(Search, { className: "absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" }),
      /* @__PURE__ */ jsx(
        Input,
        {
          value: search ?? "",
          onChange: (e) => onSearchChange?.(e.target.value),
          placeholder: searchPlaceholder,
          className: "pl-9 pr-9"
        }
      ),
      search && /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          "aria-label": "Limpar busca",
          onClick: () => onSearchChange?.(""),
          className: "absolute right-2 top-1/2 grid h-6 w-6 -translate-y-1/2 place-items-center rounded text-muted-foreground transition-colors hover:bg-accent hover:text-foreground",
          children: /* @__PURE__ */ jsx(X, { className: "h-3.5 w-3.5" })
        }
      )
    ] }),
    showChips && /* @__PURE__ */ jsx("div", { className: "flex flex-wrap items-center gap-1.5", children: chips.map((c) => {
      const Icon = c.icon;
      const active = c.key === activeChip;
      return /* @__PURE__ */ jsxs(
        "button",
        {
          type: "button",
          onClick: () => onChipChange?.(c.key),
          className: cn(
            "inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-colors",
            active ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background text-muted-foreground hover:bg-accent hover:text-foreground"
          ),
          children: [
            Icon && /* @__PURE__ */ jsx(Icon, { className: "h-3.5 w-3.5" }),
            /* @__PURE__ */ jsx("span", { children: c.label }),
            c.count !== void 0 && /* @__PURE__ */ jsx(
              "span",
              {
                className: cn(
                  "inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-xs leading-none tabular-nums",
                  active ? "bg-primary-foreground/20 text-primary-foreground" : "bg-muted text-muted-foreground"
                ),
                children: c.count
              }
            )
          ]
        },
        c.key
      );
    }) }),
    trailing && /* @__PURE__ */ jsx("div", { className: "ml-auto flex shrink-0 items-center gap-2", children: trailing })
  ] });
}
function NoAccessState({
  title = "Sem conte\xFAdo dispon\xEDvel",
  description = "Sua fun\xE7\xE3o na empresa n\xE3o tem acesso a esta \xE1rea. Fale com um administrador.",
  icon: Icon = Lock,
  className
}) {
  return /* @__PURE__ */ jsx(Card, { className: cn("border-dashed", className), children: /* @__PURE__ */ jsxs(CardContent, { className: "flex flex-col items-center justify-center gap-3 px-6 py-16 text-center", children: [
    /* @__PURE__ */ jsx("div", { className: "flex h-12 w-12 items-center justify-center rounded-full bg-muted", children: /* @__PURE__ */ jsx(Icon, { className: "h-5 w-5 text-muted-foreground", "aria-hidden": true }) }),
    /* @__PURE__ */ jsxs("div", { className: "max-w-md space-y-1", children: [
      /* @__PURE__ */ jsx("p", { className: "text-sm font-medium", children: title }),
      /* @__PURE__ */ jsx("p", { className: "text-sm text-muted-foreground", children: description })
    ] })
  ] }) });
}
function NotFoundPage({
  homeHref = "/",
  homeLabel = "Voltar ao in\xEDcio",
  title = "P\xE1gina n\xE3o encontrada",
  description = "O endere\xE7o que voc\xEA acessou n\xE3o existe ou foi movido. Volte ao in\xEDcio ou use a busca pra encontrar o que precisa.",
  secondaryAction
}) {
  const { LinkComponent } = useMirorUI();
  return /* @__PURE__ */ jsx("main", { className: "grid min-h-dvh place-items-center bg-muted/20 p-4", children: /* @__PURE__ */ jsxs(Card, { className: "w-full max-w-md", children: [
    /* @__PURE__ */ jsxs(CardHeader, { className: "items-center space-y-3 text-center", children: [
      /* @__PURE__ */ jsx("div", { className: "grid h-14 w-14 place-items-center rounded-full bg-muted", children: /* @__PURE__ */ jsx(Compass, { className: "h-7 w-7 text-muted-foreground", "aria-hidden": true }) }),
      /* @__PURE__ */ jsx("div", { className: "text-xs font-semibold uppercase tracking-wider text-muted-foreground tabular-nums", children: "404" }),
      /* @__PURE__ */ jsx(CardTitle, { className: "text-xl", children: title }),
      /* @__PURE__ */ jsx(CardDescription, { children: description })
    ] }),
    /* @__PURE__ */ jsxs(CardContent, { className: "flex flex-col items-stretch gap-2 sm:flex-row sm:justify-center", children: [
      /* @__PURE__ */ jsx(Button, { asChild: true, children: /* @__PURE__ */ jsx(LinkComponent, { to: homeHref, children: homeLabel }) }),
      secondaryAction
    ] })
  ] }) });
}
function AccessDeniedPage({
  title = "Acesso negado",
  description = "Voc\xEA n\xE3o tem permiss\xE3o para acessar esta \xE1rea.",
  alert,
  actions
}) {
  return /* @__PURE__ */ jsx("main", { className: "grid min-h-dvh place-items-center bg-muted/20 p-4", children: /* @__PURE__ */ jsxs(Card, { className: "w-full max-w-md", children: [
    /* @__PURE__ */ jsxs(CardHeader, { className: "items-center space-y-3 text-center", children: [
      /* @__PURE__ */ jsx("div", { className: "grid h-14 w-14 place-items-center rounded-full bg-muted", children: /* @__PURE__ */ jsx(Lock, { className: "h-7 w-7 text-muted-foreground", "aria-hidden": true }) }),
      /* @__PURE__ */ jsx("div", { className: "text-xs font-semibold uppercase tracking-wider text-muted-foreground tabular-nums", children: "403" }),
      /* @__PURE__ */ jsx(CardTitle, { className: "text-xl", children: title }),
      /* @__PURE__ */ jsx(CardDescription, { children: description })
    ] }),
    /* @__PURE__ */ jsxs(CardContent, { className: "space-y-4", children: [
      alert && /* @__PURE__ */ jsx(Alert, { tone: alert.tone, title: alert.title, children: alert.body }),
      actions && actions.length > 0 && /* @__PURE__ */ jsx("div", { className: "flex flex-col items-stretch gap-2 sm:flex-row sm:flex-wrap sm:justify-center", children: actions.map((a, i) => /* @__PURE__ */ jsx(ActionButton2, { ...a }, `${a.label}-${i}`)) })
    ] })
  ] }) });
}
function ActionButton2({ label, to, href, onClick, variant = "default" }) {
  const { LinkComponent } = useMirorUI();
  if (to) {
    return /* @__PURE__ */ jsx(Button, { asChild: true, variant, children: /* @__PURE__ */ jsx(LinkComponent, { to, children: label }) });
  }
  if (href) {
    return /* @__PURE__ */ jsx(Button, { asChild: true, variant, children: /* @__PURE__ */ jsx("a", { href, target: "_blank", rel: "noreferrer", children: label }) });
  }
  return /* @__PURE__ */ jsx(Button, { variant, onClick, children: label });
}
function ErrorFallback({
  error,
  reset,
  title = "Algo deu errado",
  description = "Tivemos um problema ao renderizar esta p\xE1gina. Tente novamente \u2014 se persistir, fale com o suporte.",
  extra,
  showDetails
}) {
  const message = errorMessage(error);
  return /* @__PURE__ */ jsx("main", { className: "grid min-h-dvh place-items-center bg-muted/20 p-4", children: /* @__PURE__ */ jsxs(Card, { className: "w-full max-w-md", children: [
    /* @__PURE__ */ jsxs(CardHeader, { className: "items-center space-y-3 text-center", children: [
      /* @__PURE__ */ jsx("div", { className: "grid h-14 w-14 place-items-center rounded-full bg-destructive-soft text-destructive-soft-foreground", children: /* @__PURE__ */ jsx(AlertOctagon, { className: "h-7 w-7", "aria-hidden": true }) }),
      /* @__PURE__ */ jsx(CardTitle, { className: "text-xl", children: title }),
      /* @__PURE__ */ jsx(CardDescription, { children: description })
    ] }),
    /* @__PURE__ */ jsxs(CardContent, { className: "space-y-4", children: [
      showDetails && message && /* @__PURE__ */ jsx("pre", { className: "overflow-x-auto rounded-md bg-code-bg p-3 text-xs text-code-fg", children: message }),
      /* @__PURE__ */ jsxs("div", { className: "flex flex-col items-stretch gap-2 sm:flex-row sm:justify-center", children: [
        reset && /* @__PURE__ */ jsxs(Button, { onClick: reset, children: [
          /* @__PURE__ */ jsx(RotateCw, { className: "mr-2 h-4 w-4", "aria-hidden": true }),
          "Tentar novamente"
        ] }),
        extra
      ] })
    ] })
  ] }) });
}
function errorMessage(err) {
  if (!err) return "";
  if (err instanceof Error) return err.message;
  if (typeof err === "string") return err;
  try {
    return JSON.stringify(err);
  } catch {
    return String(err);
  }
}
var ErrorBoundary = class extends Component {
  state = { error: null };
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    this.props.onError?.(error, info);
  }
  componentDidUpdate(prev) {
    if (this.state.error === null) return;
    if (!this.props.resetKeys || !prev.resetKeys) return;
    if (this.props.resetKeys.length !== prev.resetKeys.length) {
      this.reset();
      return;
    }
    for (let i = 0; i < this.props.resetKeys.length; i++) {
      if (!Object.is(this.props.resetKeys[i], prev.resetKeys[i])) {
        this.reset();
        return;
      }
    }
  }
  reset = () => {
    this.setState({ error: null });
  };
  render() {
    if (this.state.error === null) return this.props.children;
    const { fallback } = this.props;
    const { error } = this.state;
    if (typeof fallback === "function") {
      return fallback({ error, reset: this.reset });
    }
    if (fallback !== void 0) return fallback;
    return /* @__PURE__ */ jsx(ErrorFallback, { error, reset: this.reset });
  }
};
function withErrorBoundary(Comp, boundaryProps) {
  const Wrapped = (props) => /* @__PURE__ */ jsx(ErrorBoundary, { ...boundaryProps, children: /* @__PURE__ */ jsx(Comp, { ...props }) });
  Wrapped.displayName = `withErrorBoundary(${Comp.displayName ?? Comp.name ?? "Component"})`;
  return Wrapped;
}
function PageSkeleton({ variant = "dashboard", className }) {
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-6 p-4 sm:p-6", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-4", children: [
      /* @__PURE__ */ jsxs("div", { className: "space-y-2", children: [
        /* @__PURE__ */ jsx(Skeleton, { tone: "soft", className: "h-3 w-24" }),
        /* @__PURE__ */ jsx(Skeleton, { className: "h-6 w-48" })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "flex gap-2", children: [
        /* @__PURE__ */ jsx(Skeleton, { className: "h-9 w-24" }),
        /* @__PURE__ */ jsx(Skeleton, { className: "h-9 w-32" })
      ] })
    ] }),
    variant === "dashboard" && /* @__PURE__ */ jsx(DashboardBody, {}),
    variant === "list" && /* @__PURE__ */ jsx(ListBody, {}),
    variant === "detail" && /* @__PURE__ */ jsx(DetailBody, {}),
    variant === "editor" && /* @__PURE__ */ jsx(EditorBody, {})
  ] });
}
function DashboardBody() {
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx("div", { className: "grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4", children: Array.from({ length: 4 }).map((_, i) => /* @__PURE__ */ jsxs("div", { className: "rounded-xl border bg-card p-5", children: [
      /* @__PURE__ */ jsx(Skeleton, { tone: "soft", className: "h-3 w-20" }),
      /* @__PURE__ */ jsx(Skeleton, { className: "mt-3 h-7 w-24" }),
      /* @__PURE__ */ jsx(Skeleton, { tone: "soft", className: "mt-2 h-3 w-16" })
    ] }, i)) }),
    /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-1 gap-4 lg:grid-cols-2", children: [
      /* @__PURE__ */ jsxs("div", { className: "rounded-xl border bg-card p-5", children: [
        /* @__PURE__ */ jsx(Skeleton, { className: "h-4 w-32" }),
        /* @__PURE__ */ jsx(Skeleton, { className: "mt-4 h-48 w-full" })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "rounded-xl border bg-card p-5", children: [
        /* @__PURE__ */ jsx(Skeleton, { className: "h-4 w-32" }),
        /* @__PURE__ */ jsx(Skeleton, { className: "mt-4 h-48 w-full" })
      ] })
    ] })
  ] });
}
function ListBody() {
  return /* @__PURE__ */ jsxs("div", { className: "rounded-xl border bg-card", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-4 border-b px-4 py-3", children: [
      /* @__PURE__ */ jsx(Skeleton, { className: "h-8 w-64" }),
      /* @__PURE__ */ jsx(Skeleton, { tone: "soft", className: "h-8 w-24" })
    ] }),
    /* @__PURE__ */ jsx("ul", { className: "divide-y", children: Array.from({ length: 8 }).map((_, i) => /* @__PURE__ */ jsxs("li", { className: "flex items-center gap-4 px-4 py-3", children: [
      /* @__PURE__ */ jsx(Skeleton, { className: "h-9 w-9 rounded-full" }),
      /* @__PURE__ */ jsxs("div", { className: "flex-1 space-y-1.5", children: [
        /* @__PURE__ */ jsx(Skeleton, { className: "h-3.5 w-1/3" }),
        /* @__PURE__ */ jsx(Skeleton, { tone: "soft", className: "h-3 w-1/2" })
      ] }),
      /* @__PURE__ */ jsx(Skeleton, { tone: "soft", className: "h-3 w-16" })
    ] }, i)) })
  ] });
}
function DetailBody() {
  return /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-1 gap-4 lg:grid-cols-[1fr_320px]", children: [
    /* @__PURE__ */ jsxs("div", { className: "space-y-4 rounded-xl border bg-card p-5", children: [
      /* @__PURE__ */ jsx(Skeleton, { className: "h-5 w-1/2" }),
      /* @__PURE__ */ jsx(SkeletonText, { lines: 4 }),
      /* @__PURE__ */ jsx(Skeleton, { className: "h-40 w-full" }),
      /* @__PURE__ */ jsx(SkeletonText, { lines: 3 })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "space-y-4 rounded-xl border bg-card p-5", children: [
      /* @__PURE__ */ jsx(Skeleton, { className: "h-4 w-24" }),
      /* @__PURE__ */ jsx("div", { className: "space-y-2", children: Array.from({ length: 5 }).map((_, i) => /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-2", children: [
        /* @__PURE__ */ jsx(Skeleton, { tone: "soft", className: "h-3 w-20" }),
        /* @__PURE__ */ jsx(Skeleton, { className: "h-3 w-16" })
      ] }, i)) })
    ] })
  ] });
}
function EditorBody() {
  return /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-1 gap-4 lg:grid-cols-[260px_1fr]", children: [
    /* @__PURE__ */ jsx("aside", { className: "space-y-2 rounded-xl border bg-card p-3", children: Array.from({ length: 7 }).map((_, i) => /* @__PURE__ */ jsx(Skeleton, { tone: "soft", className: "h-8 w-full" }, i)) }),
    /* @__PURE__ */ jsxs("div", { className: "rounded-xl border bg-card p-5", children: [
      /* @__PURE__ */ jsx("div", { className: "flex items-center gap-2", children: Array.from({ length: 5 }).map((_, i) => /* @__PURE__ */ jsx(Skeleton, { tone: "soft", className: "h-8 w-8 rounded-md" }, i)) }),
      /* @__PURE__ */ jsxs("div", { className: "mt-4 space-y-3", children: [
        /* @__PURE__ */ jsx(Skeleton, { className: "h-6 w-1/2" }),
        /* @__PURE__ */ jsx(SkeletonText, { lines: 6 }),
        /* @__PURE__ */ jsx(Skeleton, { className: "h-32 w-full" }),
        /* @__PURE__ */ jsx(SkeletonText, { lines: 4 })
      ] })
    ] })
  ] });
}
function ModulePaywall({
  moduleName,
  description,
  icon: Icon = Lock,
  primaryAction,
  secondaryAction,
  pillLabel = "N\xE3o inclu\xEDdo no seu plano",
  body,
  className
}) {
  return /* @__PURE__ */ jsx("div", { className: cn("flex min-h-[60vh] items-center justify-center p-6", className), children: /* @__PURE__ */ jsxs("div", { className: "w-full max-w-md rounded-2xl border bg-card p-8 text-center shadow-sm", children: [
    /* @__PURE__ */ jsx("div", { className: "mx-auto mb-6 grid h-14 w-14 place-items-center rounded-2xl bg-primary/10 text-primary", children: /* @__PURE__ */ jsx(Icon, { className: "h-7 w-7" }) }),
    /* @__PURE__ */ jsx("h1", { className: "text-2xl font-semibold tracking-tight", children: moduleName }),
    description && /* @__PURE__ */ jsx("p", { className: "mt-2 text-sm leading-relaxed text-muted-foreground", children: description }),
    body && /* @__PURE__ */ jsx("div", { className: "mt-5 text-left", children: body }),
    /* @__PURE__ */ jsx("p", { className: "mt-6 text-xs uppercase tracking-wider text-muted-foreground", children: pillLabel }),
    /* @__PURE__ */ jsxs("div", { className: "mt-3 flex flex-col gap-2", children: [
      primaryAction && /* @__PURE__ */ jsx(ActionButton3, { ...primaryAction, size: "lg" }),
      secondaryAction && /* @__PURE__ */ jsx(ActionButton3, { variant: "outline", ...secondaryAction, size: "lg" })
    ] })
  ] }) });
}
function ActionButton3({
  label,
  to,
  href,
  onClick,
  variant = "default",
  icon,
  size
}) {
  const Icon = icon ?? (variant === "default" ? MessageCircle : void 0);
  const content = /* @__PURE__ */ jsxs(Fragment, { children: [
    Icon && /* @__PURE__ */ jsx(Icon, { className: "mr-2 h-5 w-5" }),
    label
  ] });
  if (to) {
    return /* @__PURE__ */ jsx(Button, { asChild: true, variant, size, className: "w-full", children: /* @__PURE__ */ jsx("a", { href: to, children: content }) });
  }
  if (href) {
    return /* @__PURE__ */ jsx(Button, { asChild: true, variant, size, className: "w-full", children: /* @__PURE__ */ jsx("a", { href, target: "_blank", rel: "noopener noreferrer", children: content }) });
  }
  return /* @__PURE__ */ jsx(Button, { variant, size, className: "w-full", onClick, children: content });
}
function ModulePaywallSkeleton({ className }) {
  return /* @__PURE__ */ jsx("div", { className: cn("flex min-h-[60vh] items-center justify-center p-6", className), children: /* @__PURE__ */ jsxs("div", { className: "w-full max-w-md rounded-2xl border bg-card p-8 shadow-sm", children: [
    /* @__PURE__ */ jsx(Skeleton, { className: "mx-auto h-14 w-14 rounded-2xl" }),
    /* @__PURE__ */ jsx(Skeleton, { className: "mx-auto mt-6 h-6 w-40" }),
    /* @__PURE__ */ jsx(Skeleton, { tone: "soft", className: "mx-auto mt-2 h-3 w-56" }),
    /* @__PURE__ */ jsx(Skeleton, { tone: "soft", className: "mx-auto mt-1.5 h-3 w-48" }),
    /* @__PURE__ */ jsx(Skeleton, { tone: "soft", className: "mx-auto mt-6 h-3 w-28" }),
    /* @__PURE__ */ jsx(Skeleton, { className: "mt-3 h-11 w-full rounded-md" })
  ] }) });
}
var Grid3x3Icon = forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsxs(
    "svg",
    {
      ref,
      viewBox: "0 0 20 20",
      fill: "currentColor",
      "aria-hidden": "true",
      className,
      ...props,
      children: [
        /* @__PURE__ */ jsx("circle", { cx: "4", cy: "4", r: "1.6" }),
        /* @__PURE__ */ jsx("circle", { cx: "10", cy: "4", r: "1.6" }),
        /* @__PURE__ */ jsx("circle", { cx: "16", cy: "4", r: "1.6" }),
        /* @__PURE__ */ jsx("circle", { cx: "4", cy: "10", r: "1.6" }),
        /* @__PURE__ */ jsx("circle", { cx: "10", cy: "10", r: "1.6" }),
        /* @__PURE__ */ jsx("circle", { cx: "16", cy: "10", r: "1.6" }),
        /* @__PURE__ */ jsx("circle", { cx: "4", cy: "16", r: "1.6" }),
        /* @__PURE__ */ jsx("circle", { cx: "10", cy: "16", r: "1.6" }),
        /* @__PURE__ */ jsx("circle", { cx: "16", cy: "16", r: "1.6" })
      ]
    }
  )
);
Grid3x3Icon.displayName = "Grid3x3Icon";
function AppHubCard({ item, isLaunching, onClick, autoFocus }) {
  const accent = item.accentColor ?? "oklch(0.62 0.16 250)";
  const isComingSoon = !!item.comingSoon;
  return /* @__PURE__ */ jsxs(
    "button",
    {
      type: "button",
      onClick: isComingSoon ? void 0 : onClick,
      autoFocus,
      disabled: isLaunching || isComingSoon,
      "aria-disabled": isComingSoon || void 0,
      "aria-label": isComingSoon ? `${item.name} \u2014 em breve` : `Abrir ${item.name}${item.companyName ? `, ${item.companyName}` : ""}`,
      className: cn(
        "group relative flex h-32 w-full flex-col items-center justify-center gap-2 rounded-xl border border-border/60 bg-muted/40 px-2 py-2 outline-none transition-all duration-200",
        "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-popover",
        // Hover só quando o card é interativo (teaser "Em Breve" fica estático).
        !isComingSoon && "hover:-translate-y-0.5 hover:border-border hover:bg-muted hover:shadow-sm",
        isComingSoon ? "cursor-default opacity-70" : "disabled:cursor-wait disabled:opacity-80",
        item.isUpsell && !isComingSoon && "opacity-70 hover:opacity-100"
      ),
      style: {
        // Halo radial atrás do ícone, só aparece em hover (CSS custom prop).
        ["--accent"]: accent
      },
      children: [
        /* @__PURE__ */ jsx(
          "span",
          {
            "aria-hidden": true,
            className: "pointer-events-none absolute inset-0 rounded-xl opacity-0 transition-opacity duration-200 group-hover:opacity-100",
            style: {
              background: "radial-gradient(circle at 50% 30%, color-mix(in oklab, var(--accent) 18%, transparent), transparent 60%)"
            }
          }
        ),
        item.isCurrent && /* @__PURE__ */ jsx(
          "span",
          {
            "aria-hidden": true,
            className: "absolute left-1.5 top-1.5 flex items-center gap-1 text-[10px] font-medium text-success",
            children: /* @__PURE__ */ jsxs("span", { className: "relative inline-flex h-1.5 w-1.5", children: [
              /* @__PURE__ */ jsx("span", { className: "absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-60" }),
              /* @__PURE__ */ jsx("span", { className: "relative inline-flex h-1.5 w-1.5 rounded-full bg-success" })
            ] })
          }
        ),
        isComingSoon ? /* @__PURE__ */ jsx("span", { className: "absolute right-1.5 top-1.5 rounded-full bg-warning-soft px-1.5 py-px text-[9px] font-medium uppercase tracking-wide text-warning-soft-foreground", children: "Em breve" }) : item.statusLabel && /* @__PURE__ */ jsx("span", { className: "absolute right-1.5 top-1.5 rounded-full bg-muted px-1.5 py-px text-[9px] font-medium uppercase tracking-wide text-muted-foreground", children: item.statusLabel }),
        /* @__PURE__ */ jsx(
          "span",
          {
            "aria-hidden": true,
            className: "relative grid h-16 w-16 place-items-center text-foreground",
            style: { color: item.isCurrent ? accent : void 0 },
            children: isLaunching ? /* @__PURE__ */ jsx(Loader2, { className: "h-7 w-7 animate-spin" }) : item.logoUrl ? /* @__PURE__ */ jsx(
              "img",
              {
                src: item.logoUrl,
                alt: "",
                loading: "lazy",
                className: "h-16 w-16 object-contain"
              }
            ) : /* @__PURE__ */ jsx("span", { className: "grid h-16 w-16 place-items-center [&>svg]:h-9 [&>svg]:w-9", children: item.icon })
          }
        ),
        /* @__PURE__ */ jsx("span", { className: "relative line-clamp-2 px-1 text-center text-[11.5px] font-medium leading-tight", children: isLaunching ? "abrindo\u2026" : item.name }),
        !isLaunching && item.companyName && /* @__PURE__ */ jsx("span", { className: "relative -mt-1 truncate text-[9.5px] tabular-nums text-muted-foreground", children: item.companyName })
      ]
    }
  );
}
function AppHubGrid({ items, launchingKey, onLaunch, className }) {
  return /* @__PURE__ */ jsx(
    "div",
    {
      role: "grid",
      "aria-label": "Apps dispon\xEDveis",
      className: cn("grid grid-cols-2 gap-2 sm:grid-cols-3", className),
      children: /* @__PURE__ */ jsx(Stagger, { delay: 30, children: items.map((item, idx) => /* @__PURE__ */ jsx("div", { role: "gridcell", children: /* @__PURE__ */ jsx(
        AppHubCard,
        {
          item,
          isLaunching: launchingKey === item.key,
          onClick: () => onLaunch(item),
          autoFocus: idx === 0
        }
      ) }, item.key)) })
    }
  );
}
function AppHubEmpty({
  salesContactHref,
  ctaLabel = "Conhecer outros apps",
  headline = "Sua su\xEDte ainda \xE9 enxuta",
  subtext = "Adicione novos apps Miror para tarefas, processos e mais."
}) {
  return /* @__PURE__ */ jsxs("div", { className: "relative overflow-hidden rounded-xl border border-dashed border-border bg-muted/30 px-4 py-6 text-center", children: [
    /* @__PURE__ */ jsx(
      "span",
      {
        "aria-hidden": true,
        className: "pointer-events-none absolute inset-0",
        style: {
          background: "radial-gradient(circle at 50% 0%, color-mix(in oklab, oklch(0.62 0.16 250) 14%, transparent), transparent 60%)"
        }
      }
    ),
    /* @__PURE__ */ jsx(
      Sparkles,
      {
        className: "relative mx-auto h-6 w-6 text-muted-foreground",
        "aria-hidden": true
      }
    ),
    /* @__PURE__ */ jsx("p", { className: "relative mt-2 text-sm font-medium leading-tight", children: headline }),
    /* @__PURE__ */ jsx("p", { className: "relative mt-1 text-xs text-muted-foreground", children: subtext }),
    salesContactHref && /* @__PURE__ */ jsx(
      "a",
      {
        href: salesContactHref,
        target: "_blank",
        rel: "noopener noreferrer",
        className: "relative mt-3 inline-flex h-8 items-center rounded-md border bg-card px-3 text-xs font-medium transition-colors hover:bg-accent hover:text-accent-foreground",
        children: ctaLabel
      }
    )
  ] });
}
function AppUrlOverridesSheet({
  open,
  onClose,
  apps,
  overrides,
  onSetOverride,
  onClearOverride,
  onClearAll
}) {
  const [drafts, setDrafts] = useState({});
  const [errors, setErrors] = useState({});
  useEffect(() => {
    if (open) {
      setDrafts({ ...overrides });
      setErrors({});
    }
  }, [open, overrides]);
  const handleChange = (key, value) => {
    setDrafts((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      const { [key]: _omit, ...rest } = prev;
      return rest;
    });
  };
  const handleResetRow = (key) => {
    setDrafts((prev) => {
      const { [key]: _omit, ...rest } = prev;
      return rest;
    });
    setErrors((prev) => {
      const { [key]: _omit, ...rest } = prev;
      return rest;
    });
  };
  const handleSave = () => {
    const newErrors = {};
    for (const app of apps) {
      const value = (drafts[app.key] ?? "").trim();
      if (value === "") continue;
      try {
        const u = new URL(value);
        if (u.protocol !== "http:" && u.protocol !== "https:") {
          newErrors[app.key] = "Use http:// ou https://";
        }
      } catch {
        newErrors[app.key] = "URL inv\xE1lida";
      }
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    for (const app of apps) {
      const value = (drafts[app.key] ?? "").trim();
      if (value === "") {
        if (app.key in overrides) onClearOverride(app.key);
      } else if (value !== overrides[app.key]) {
        onSetOverride(app.key, value);
      }
    }
    onClose();
  };
  const handleClearAll = () => {
    if (confirm("Remover todos os overrides de URL?")) {
      onClearAll();
      setDrafts({});
      setErrors({});
    }
  };
  return /* @__PURE__ */ jsx(
    SlideOver,
    {
      open,
      onClose,
      title: "URLs de desenvolvimento",
      description: "Apenas voc\xEA (super_admin) v\xEA isso. Os overrides ficam neste navegador, n\xE3o tocam a configura\xE7\xE3o do servidor.",
      size: "lg",
      footer: /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(
          Button,
          {
            type: "button",
            variant: "ghost",
            size: "sm",
            onClick: handleClearAll,
            className: "mr-auto text-muted-foreground hover:text-destructive",
            children: "Limpar todos"
          }
        ),
        /* @__PURE__ */ jsx(Button, { type: "button", variant: "outline", size: "sm", onClick: onClose, children: "Cancelar" }),
        /* @__PURE__ */ jsx(Button, { type: "button", size: "sm", onClick: handleSave, children: "Salvar" })
      ] }),
      children: /* @__PURE__ */ jsx("div", { className: "space-y-5 p-5", children: apps.length === 0 ? /* @__PURE__ */ jsx("p", { className: "text-sm text-muted-foreground", children: "Sem apps dispon\xEDveis para configurar." }) : apps.map((app) => {
        const draft = drafts[app.key] ?? "";
        const hasOverride = app.key in overrides;
        const error = errors[app.key];
        return /* @__PURE__ */ jsxs("div", { className: "space-y-1.5", children: [
          /* @__PURE__ */ jsxs("div", { className: "flex items-baseline justify-between gap-2", children: [
            /* @__PURE__ */ jsx(Label, { htmlFor: `override-${app.key}`, className: "text-sm font-medium", children: app.name }),
            hasOverride && /* @__PURE__ */ jsx("span", { className: "text-[10px] font-medium uppercase tracking-wide text-warning", children: "Override ativo" })
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1.5", children: [
            /* @__PURE__ */ jsx(
              Input,
              {
                id: `override-${app.key}`,
                type: "url",
                inputMode: "url",
                autoComplete: "off",
                spellCheck: false,
                placeholder: app.baseUrl,
                value: draft,
                onChange: (e) => handleChange(app.key, e.target.value),
                className: cn(error && "border-destructive focus-visible:ring-destructive")
              }
            ),
            /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                onClick: () => handleResetRow(app.key),
                "aria-label": `Reverter URL de ${app.name} ao cat\xE1logo`,
                title: "Reverter ao cat\xE1logo",
                className: "grid h-9 w-9 shrink-0 place-items-center rounded-md border text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                children: /* @__PURE__ */ jsx(RotateCcw, { className: "h-3.5 w-3.5" })
              }
            )
          ] }),
          error ? /* @__PURE__ */ jsx("p", { className: "text-[11px] text-destructive", children: error }) : /* @__PURE__ */ jsxs("p", { className: "text-[11px] text-muted-foreground tabular-nums", children: [
            "Cat\xE1logo: ",
            /* @__PURE__ */ jsx("span", { className: "font-mono", children: app.baseUrl })
          ] })
        ] }, app.key);
      }) })
    }
  );
}
function AppHubLauncher({
  items,
  isLoading,
  error,
  onRetry,
  activeCompanyName,
  onLaunch,
  launchingKey,
  footer,
  salesContactHref,
  triggerLabel = "Apps da Su\xEDte",
  triggerClassName,
  isSuperAdmin,
  urlOverrides = {},
  onSetOverride,
  onClearOverride,
  onClearAll
}) {
  const [open, setOpen] = useState(false);
  const [overridesOpen, setOverridesOpen] = useState(false);
  const hasOverrides = Object.keys(urlOverrides).length > 0;
  const visibleItems = items;
  const nonCurrentCount = visibleItems.filter((i) => !i.isCurrent).length;
  const showEmpty = !isLoading && !error && nonCurrentCount === 0;
  const handleOpenOverrides = () => {
    setOpen(false);
    setOverridesOpen(true);
  };
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs(Popover, { open, onOpenChange: setOpen, children: [
      /* @__PURE__ */ jsxs(Tooltip, { children: [
        /* @__PURE__ */ jsx(TooltipTrigger, { asChild: true, children: /* @__PURE__ */ jsx(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsxs(
          "button",
          {
            type: "button",
            "aria-label": triggerLabel,
            className: cn(
              "relative grid h-9 w-9 place-items-center rounded-md text-muted-foreground transition-colors",
              "hover:bg-accent hover:text-foreground",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              "data-[state=open]:bg-accent data-[state=open]:text-foreground",
              triggerClassName
            ),
            children: [
              /* @__PURE__ */ jsx(Grid3x3Icon, { className: "h-4 w-4" }),
              hasOverrides && isSuperAdmin && /* @__PURE__ */ jsx(
                "span",
                {
                  "aria-hidden": true,
                  title: "H\xE1 overrides de URL ativos (modo dev)",
                  className: "absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-warning"
                }
              )
            ]
          }
        ) }) }),
        /* @__PURE__ */ jsx(TooltipContent, { side: "bottom", children: triggerLabel })
      ] }),
      /* @__PURE__ */ jsxs(
        PopoverContent,
        {
          align: "end",
          sideOffset: 10,
          className: "w-[360px] max-w-[calc(100vw-2rem)] p-0",
          children: [
            /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between gap-2 border-b px-4 py-3", children: [
              /* @__PURE__ */ jsxs("div", { className: "min-w-0 flex-1", children: [
                /* @__PURE__ */ jsx("p", { className: "text-sm font-semibold leading-tight", children: "Apps da Su\xEDte" }),
                activeCompanyName && /* @__PURE__ */ jsxs("p", { className: "mt-0.5 truncate text-[11px] text-muted-foreground tabular-nums", children: [
                  activeCompanyName,
                  nonCurrentCount > 0 ? ` \xB7 ${visibleItems.length} apps` : ""
                ] })
              ] }),
              isSuperAdmin && onSetOverride && /* @__PURE__ */ jsxs(Tooltip, { children: [
                /* @__PURE__ */ jsx(TooltipTrigger, { asChild: true, children: /* @__PURE__ */ jsx(
                  "button",
                  {
                    type: "button",
                    onClick: handleOpenOverrides,
                    "aria-label": "Configurar URLs de desenvolvimento",
                    className: cn(
                      "grid h-7 w-7 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors",
                      "hover:bg-accent hover:text-foreground",
                      "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                      hasOverrides && "text-warning hover:text-warning"
                    ),
                    children: /* @__PURE__ */ jsx(Settings2, { className: "h-3.5 w-3.5" })
                  }
                ) }),
                /* @__PURE__ */ jsx(TooltipContent, { side: "bottom", children: "URLs de desenvolvimento" })
              ] })
            ] }),
            isSuperAdmin && hasOverrides && /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 border-b bg-warning-soft px-4 py-2 text-[11px] text-warning-soft-foreground", children: [
              /* @__PURE__ */ jsx(TriangleAlert, { className: "h-3.5 w-3.5 shrink-0" }),
              /* @__PURE__ */ jsxs("span", { children: [
                "Voc\xEA est\xE1 usando URLs locais (",
                Object.keys(urlOverrides).length,
                " ",
                Object.keys(urlOverrides).length === 1 ? "app" : "apps",
                ")."
              ] })
            ] }),
            /* @__PURE__ */ jsx("div", { className: "p-4", children: isLoading ? /* @__PURE__ */ jsx("div", { className: "grid grid-cols-2 gap-2 sm:grid-cols-3", children: Array.from({ length: 6 }).map((_, i) => /* @__PURE__ */ jsx(Skeleton, { className: "h-24 w-full rounded-xl" }, i)) }) : error ? /* @__PURE__ */ jsxs("div", { className: "rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-5 text-center", children: [
              /* @__PURE__ */ jsx("p", { className: "text-sm font-medium text-destructive", children: "N\xE3o foi poss\xEDvel carregar os apps." }),
              onRetry && /* @__PURE__ */ jsx(
                "button",
                {
                  type: "button",
                  onClick: () => onRetry(),
                  className: "mt-2 text-xs font-medium text-destructive underline-offset-2 hover:underline",
                  children: "Tentar de novo"
                }
              )
            ] }) : showEmpty ? /* @__PURE__ */ jsx(AppHubEmpty, { salesContactHref }) : /* @__PURE__ */ jsx(
              AppHubGrid,
              {
                items: visibleItems,
                launchingKey,
                onLaunch: (item) => {
                  void onLaunch(item);
                }
              }
            ) }),
            footer && /* @__PURE__ */ jsx("div", { className: "border-t bg-muted/30 px-4 py-2.5 text-xs", children: footer })
          ]
        }
      )
    ] }),
    isSuperAdmin && onSetOverride && onClearOverride && onClearAll && /* @__PURE__ */ jsx(
      AppUrlOverridesSheet,
      {
        open: overridesOpen,
        onClose: () => setOverridesOpen(false),
        apps: items.map((i) => ({ key: i.key, name: i.name, baseUrl: i.baseUrl })),
        overrides: urlOverrides,
        onSetOverride,
        onClearOverride,
        onClearAll
      }
    )
  ] });
}

export { AccessDeniedPage, Alert, AppHeaderBar, AppHubCard, AppHubEmpty, AppHubGrid, AppHubLauncher, AppLogoFallback, AppShell, AppUrlOverridesSheet, Badge, Breadcrumb, Button, Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle, CepInput, CepLookupError, CommandPalette, CompanySwitcher, Dialog, DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuPortal, DropdownMenuSeparator, DropdownMenuTrigger, EmptyState, ErrorBoundary, ErrorFallback, FilterBar, FilteredEmpty, Grid3x3Icon, Input, KeyboardHint, Label, MetricCard, MirorPageMetaProvider, MirorPulse, MirorPulseWithLabel, MirorUIProvider, ModulePaywall, ModulePaywallSkeleton, NoAccessState, NotFoundPage, PageHeader, PageSkeleton, PinInput, Popover, PopoverAnchor, PopoverContent, PopoverTrigger, SessionGate, SidebarAppsSection, SidebarUserFooter, Skeleton, SkeletonList, SkeletonText, SlideOver, Stagger, StatusBadge, Switch, Tabs, Textarea, ThemeToggleButton, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, TourHelpButton, UserMenu, badgeVariants, buttonVariants, formatCep, isCompleteCep, lookupCep, useHasPageMetaProvider, useMinimumDuration, useMirorBrand, useMirorPageMeta, useMirorUI, useSetPageMeta, withErrorBoundary };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map